// playback.ts — WebCodecs-based MP4 player with b2 SEI metadata extraction.
//
// Originated as a port of Brad's playback.js (~/Developer/git/dragon/html/playback.js).
// Key changes versus the source:
//  - Refs-object API instead of `document.getElementById` (decision [#9]).
//  - TypeScript with explicit types throughout (decision [#10]).
//  - AbortController + generation token + idempotent destroy() for React
//    lifecycle safety (decision [#5]).
//  - Non-fragmented MP4 reader (stbl/stsc/stco/co64/stsz/stts/ctts/stss)
//    added alongside Brad's fragmented walker (decision [#2]).
//  - Brad's JSON metadata track is removed; b2 SEI is the metadata source
//    (decision [#1, #4]).
//  - Canvas-drawn HUD using SEI cameraName + timestamp (decisions [#13, #3]).
//  - Per-frame LPR overlay (Phase 4).
import { parseB2FromSample, } from "./b2-sei";
import { Mp4Muxer } from "./mp4-muxer";
// B2_EXT_OD_V2.color is a uint32 chosen by aiengine for that class. The
// byte order is ARGB on host (x86_64 LE → readU32LE produces 0xAARRGGBB).
// Map to a canvas rgba() string. When alpha=0 we treat that as "viewer
// picks the alpha" (aiengine often writes only RGB and leaves the alpha
// byte zero) and default to 0.95 so the rectangle is visible. A truly
// transparent class (alpha=0 intentional) is indistinguishable from
// "engine didn't set alpha" in the wire format, so this defaults to
// visible — a draw at alpha=0 would just be a no-op stroke, which is
// worse than a slightly-too-visible box.
function odV2Color(packed) {
    const a = (packed >>> 24) & 0xFF;
    const r = (packed >>> 16) & 0xFF;
    const g = (packed >>> 8) & 0xFF;
    const b = packed & 0xFF;
    const alpha = a === 0 ? 0.95 : a / 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
export class Playback {
    // Public-ish for tests + DragonPlayer wrapper introspection.
    canvas;
    ctx;
    videoTrack = null;
    videoSamples = [];
    durationSec = 0;
    totalSize = 0;
    loadComplete = false;
    // Explicit state machine for the load pipeline. Gates play() / seek() /
    // exportWithOverlay() / updateBuffered() so streaming-body (where bytes
    // are still arriving asynchronously) doesn't trip on machinery that
    // assumes loadComplete. Transitions are one-way through:
    //   initial → head-fetched → moov-parsed → streaming-body → body-complete
    // with `error` as a terminal absorbing state. `body-complete` also
    // implies `loadComplete = true` for backward-compat with existing callers.
    loadState = "initial";
    playing = false;
    playheadSec = 0;
    // Guards the one-shot onEnded callback; cleared by seek().
    endReported = false;
    // Refs / DOM
    overlayEL;
    playBtn;
    seekEL;
    timeEL;
    exportBtn;
    exportStatus;
    exporting = false;
    cancelExport = false;
    // Source
    fileUrl;
    runtimeOpts;
    // Body buffer + parse cursor (used by fragmented path; non-frag path reads
    // sample-by-sample via Range requests).
    bodyOffset = 0;
    bodyLen = 0;
    bodyBytes = null;
    parseCursor = 0;
    isFragmented = false;
    moovEnd = 0;
    // For non-frag: keep the head buffer (moov region) and stream the rest
    // exactly as fragmented mode does — sample slices come from bodyBytes.
    // Per decision [#18], single-buffer model is acceptable for MVP.
    // Decoder + playback state
    decoder = null;
    playheadAtStart = 0;
    startWallTime = 0;
    rafHandle = null;
    nextVideoIdx = 0;
    // Seconds of the clip whose bytes have actually arrived. Public because a
    // caller scheduling a seek (DragonPlayer's citation offset) must know when
    // the target is fetchable; seeking earlier silently clamps to this cursor.
    bufferedMaxSec = 0;
    // False while a fragmented clip's real length is still being read from its
    // tail index. Playback and the seek bar wait on it (like YouTube, the bar
    // never moves against a guessed length); non-fragmented clips know theirs
    // from the moov and start settled.
    durationSettled = true;
    // The clip length the UI may draw against: 0 until known. A fragmented clip
    // reports 0 until its tail index settles (ADR-172), so seek-bar markers are
    // never placed against a guessed length.
    // A byte-share estimate (tail index unreadable) is a guess, not a length,
    // until the body completes and the downloaded length becomes the real one.
    get knownDurationSec() {
        if (!this.durationSettled || this.durationSec <= 0)
            return 0;
        if (this.durationEstimated && this.loadState !== "body-complete")
            return 0;
        return this.durationSec;
    }
    // Set when the tail index could not be read: durationSec is then estimated
    // from the share of bytes downloaded instead of the downloaded length.
    durationEstimated = false;
    // Tail of the file (suffix Range), requested in parallel with the head so
    // the fragmented length costs no extra round trip before playback.
    tailFetch = null;
    abortController;
    frameQueue = [];
    seekPending = false;
    minAcceptTsMicros = 0;
    needsKeyframe = false;
    // Streaming-buffered cursor (B2). Tracks the highest sample index whose
    // bytes are fully present in bodyBytes. Monotonic; never re-scanned from
    // 0. Only meaningful in streaming-body state; body-complete uses the
    // last-sample shortcut.
    bufferedSampleIdx = 0;
    // Autoplay gating (B3). play() called before minPlayableSeconds of buffer
    // sets pendingPlay = true; updateBuffered() retries when the cursor
    // catches up. firstFrameWatchdog fires if 10s pass without firstFrameSeen.
    static MIN_PLAYABLE_SECONDS = 2;
    // Longest a fragmented clip waits for its tail index before falling back to
    // the byte-share estimate. Two relay round trips are ~1-2 s in prod.
    static DURATION_PROBE_TIMEOUT_MS = 5000;
    static FIRST_FRAME_TIMEOUT_MS = 10_000;
    pendingPlay = false;
    // Last value sent to runtimeOpts.onPlayingChange; a deferred autoplay
    // (pendingPlay) counts as playing because the reviewer asked for it.
    reportedPlaying = false;
    // True between the seek bar's first `input` and its `change` (drag release).
    scrubbing = false;
    reportPlaying() {
        const value = this.playing || this.pendingPlay;
        if (value === this.reportedPlaying)
            return;
        this.reportedPlaying = value;
        try {
            this.runtimeOpts.onPlayingChange?.(value);
        }
        catch { /* noop */ }
    }
    firstFrameWatchdog = null;
    // Memory ceiling for in-memory non-frag streaming (B6 + decision [#18]).
    // appendBody refuses growth past this. 75 MB covers a ~5 min clip at
    // ~2 Mbps, which is the documented pilot upper bound.
    static BODY_BYTES_CEILING = 75 * 1024 * 1024;
    // SEI metadata cache, keyed by sample CTS in microseconds (matches
    // EncodedVideoChunk.timestamp + VideoFrame.timestamp). Decision [#20]:
    // cts, not sample index, so B-frame display order resolves correctly.
    seiByCtsMicros = new Map();
    lastSeiSeen = null;
    // Lifecycle
    destroyed = false;
    generation;
    static genCounter = 0;
    // Class-level last-known-good hwAccel cache. Keyed by codec+resolution
    // (description bytes are typically identical for clips coming off the
    // same camera + codec settings, so codec+W×H is a sufficient fingerprint).
    // First paint via handleFrame writes the active hint; subsequent Playback
    // instances starting with the same fingerprint use it as the starting
    // hwAccel. cs2347's first cs2347 clip after page-load still eats one
    // decoder-error fallthrough; clips 2..N skip it. Bounded at 16 entries
    // — more than enough for the customer × camera × codec matrix; LRU-evicts
    // the oldest insert when full.
    static hwAccelGood = new Map();
    static HW_ACCEL_CACHE_MAX = 16;
    // Set of codec|WxH fingerprints whose mp4 avcC blob has a stale/mismatched
    // SPS vs the in-band one — observed on cs2347 cam2 (3072x1728 avc1.640032):
    // mp4 avcC declared num_ref_frames=4, in-band SPS used 1. Configuring the
    // decoder with the avcC failed to decode ANY chunk under all 3 hwAccel
    // hints. The fix is to rebuild avcC from the in-band SPS/PPS that the
    // slice data actually references. Once rebuilt, decode works on first
    // try — recordRequiresRebuiltAvcC flags the fingerprint so subsequent
    // Playback instances skip the original-avcC attempt.
    static requiresRebuiltAvcC = new Set();
    static cacheKey(codec, width, height) {
        return `${codec}|${width}x${height}`;
    }
    static recordWorkingHwAccel(codec, width, height, hint) {
        const key = Playback.cacheKey(codec, width, height);
        // Re-insert to move-to-end (Map iteration order = insertion order).
        if (Playback.hwAccelGood.has(key))
            Playback.hwAccelGood.delete(key);
        Playback.hwAccelGood.set(key, hint);
        while (Playback.hwAccelGood.size > Playback.HW_ACCEL_CACHE_MAX) {
            const oldest = Playback.hwAccelGood.keys().next().value;
            if (oldest === undefined)
                break;
            Playback.hwAccelGood.delete(oldest);
        }
    }
    static recordRequiresRebuiltAvcC(codec, width, height) {
        Playback.requiresRebuiltAvcC.add(Playback.cacheKey(codec, width, height));
    }
    static _testClearHwAccelCache() {
        Playback.hwAccelGood.clear();
        Playback.requiresRebuiltAvcC.clear();
    }
    static _testGetHwAccelCache() {
        return new Map(Playback.hwAccelGood);
    }
    static _testGetRebuildAvcCSet() {
        return new Set(Playback.requiresRebuiltAvcC);
    }
    boundSeekInput;
    boundSeekChange;
    boundPlayClick;
    boundExportClick;
    seekDebounceTimer = null;
    constructor(opts, fileUrl, runtimeOpts = {}) {
        this.canvas = opts.canvas;
        const ctx = opts.canvas.getContext("2d");
        if (!ctx)
            throw new Error("2d context unavailable on canvas");
        this.ctx = ctx;
        this.overlayEL = opts.overlay ?? null;
        this.playBtn = opts.playBtn ?? null;
        this.seekEL = opts.seekEL ?? null;
        this.timeEL = opts.timeEL ?? null;
        this.exportBtn = opts.exportBtn ?? null;
        this.exportStatus = opts.exportStatus ?? null;
        this.fileUrl = fileUrl;
        this.runtimeOpts = runtimeOpts;
        this.generation = ++Playback.genCounter;
        this.abortController = new AbortController();
        this.bindUiHandlers();
        this.setOverlay("Loading…");
        this.load().catch((e) => {
            if (this.destroyed)
                return;
            const msg = e instanceof Error ? e.message : String(e);
            // eslint-disable-next-line no-console
            console.error("[playback] load failed:", msg);
            this.setOverlay("Error: " + msg);
            this.runtimeOpts.onError?.();
        });
    }
    // ---- Destroy ------------------------------------------------------------
    // Idempotent destroy — safe under React strict-mode double-mount.
    destroy() {
        if (this.destroyed)
            return;
        this.destroyed = true;
        try {
            this.abortController.abort();
        }
        catch { /* noop */ }
        if (this.rafHandle !== null) {
            cancelAnimationFrame(this.rafHandle);
            this.rafHandle = null;
        }
        if (this.seekDebounceTimer !== null) {
            clearTimeout(this.seekDebounceTimer);
            this.seekDebounceTimer = null;
        }
        if (this.firstFrameWatchdog !== null) {
            clearTimeout(this.firstFrameWatchdog);
            this.firstFrameWatchdog = null;
        }
        if (this.decoder && this.decoder.state !== "closed") {
            try {
                this.decoder.close();
            }
            catch { /* noop */ }
        }
        this.decoder = null;
        while (this.frameQueue.length > 0) {
            try {
                this.frameQueue.shift().close();
            }
            catch { /* noop */ }
        }
        if (this.playBtn && this.boundPlayClick) {
            this.playBtn.removeEventListener("click", this.boundPlayClick);
        }
        if (this.seekEL && this.boundSeekInput) {
            this.seekEL.removeEventListener("input", this.boundSeekInput);
        }
        if (this.seekEL && this.boundSeekChange) {
            this.seekEL.removeEventListener("change", this.boundSeekChange);
        }
        if (this.exportBtn && this.boundExportClick) {
            this.exportBtn.removeEventListener("click", this.boundExportClick);
        }
        this.seiByCtsMicros.clear();
        this.bodyBytes = null;
    }
    // ---- UI binding ---------------------------------------------------------
    bindUiHandlers() {
        if (this.playBtn) {
            this.boundPlayClick = () => this.toggle();
            this.playBtn.addEventListener("click", this.boundPlayClick);
        }
        if (this.seekEL) {
            const doSeek = () => {
                if (!this.durationSec || !this.seekEL)
                    return;
                const frac = parseInt(this.seekEL.value, 10) / 1000;
                this.seek(frac * this.durationSec);
            };
            this.boundSeekInput = () => {
                if (!this.scrubbing) {
                    this.scrubbing = true;
                    try {
                        this.runtimeOpts.onScrubChange?.(true);
                    }
                    catch { /* noop */ }
                }
                if (this.seekDebounceTimer)
                    clearTimeout(this.seekDebounceTimer);
                this.seekDebounceTimer = setTimeout(doSeek, 120);
            };
            this.boundSeekChange = () => {
                if (this.seekDebounceTimer)
                    clearTimeout(this.seekDebounceTimer);
                doSeek();
                if (this.scrubbing) {
                    this.scrubbing = false;
                    try {
                        this.runtimeOpts.onScrubChange?.(false);
                    }
                    catch { /* noop */ }
                }
            };
            this.seekEL.addEventListener("input", this.boundSeekInput);
            this.seekEL.addEventListener("change", this.boundSeekChange);
        }
        if (this.exportBtn) {
            this.boundExportClick = () => {
                if (this.exporting) {
                    // Second click while a job is running = cancel request. Worker
                    // checks this.cancelExport at every backpressure tick.
                    this.cancelExport = true;
                    return;
                }
                // Fire-and-forget — runExport handles its own status updates.
                void this.exportWithOverlay();
            };
            this.exportBtn.addEventListener("click", this.boundExportClick);
        }
    }
    setOverlay(msg) {
        if (this.overlayEL)
            this.overlayEL.textContent = msg;
    }
    // ---- Byte readers -------------------------------------------------------
    readU32(buf, o) {
        return ((buf[o] << 24) | (buf[o + 1] << 16) | (buf[o + 2] << 8) | buf[o + 3]) >>> 0;
    }
    readU16(buf, o) {
        return ((buf[o] << 8) | buf[o + 1]) >>> 0;
    }
    readU64(buf, o) {
        return this.readU32(buf, o) * 4294967296 + this.readU32(buf, o + 4);
    }
    readType(buf, o) {
        return String.fromCharCode(buf[o], buf[o + 1], buf[o + 2], buf[o + 3]);
    }
    // Walk direct children of a container box; fn(type, start, end, hdr).
    walkBoxes(buf, start, end, fn) {
        let p = start;
        while (p + 8 <= end) {
            const size = this.readU32(buf, p);
            const type = this.readType(buf, p + 4);
            let boxEnd;
            let hdr = 8;
            if (size === 1) {
                if (p + 16 > end)
                    break;
                boxEnd = p + this.readU64(buf, p + 8);
                hdr = 16;
            }
            else if (size === 0) {
                boxEnd = end;
            }
            else {
                boxEnd = p + size;
            }
            if (boxEnd > end || boxEnd <= p)
                break;
            fn(type, p, boxEnd, hdr);
            p = boxEnd;
        }
    }
    // ---- Load pipeline ------------------------------------------------------
    async load() {
        // Snapshot the generation at entry; bail after every await if the
        // instance was destroyed mid-load (React strict-mode double-mount,
        // user clicked away, src changed). Without this, a torn-down instance
        // can still allocate buffers and construct a decoder.
        const gen = this.generation;
        const stillLive = () => !this.destroyed && this.generation === gen;
        // Probe head (ftyp + moov). 64KB plenty for fragmented; non-frag has
        // moov AFTER mdat so we'll fetch it lazily.
        const probeLen = 65536;
        this.tailFetch = fetch(this.fileUrl, {
            headers: { Range: `bytes=-${probeLen}` },
            signal: this.abortController.signal,
        }).then(async (r) => {
            const m = (r.headers.get("Content-Range") ?? "").match(/bytes (\d+)-\d+\/(\d+)$/);
            if (r.status !== 206 || !m) {
                void r.body?.cancel().catch(() => { });
                return null;
            }
            return { bytes: new Uint8Array(await r.arrayBuffer()), start: parseInt(m[1], 10), total: parseInt(m[2], 10) };
        }).catch(() => null);
        const probeResp = await fetch(this.fileUrl, {
            headers: { Range: `bytes=0-${probeLen - 1}` },
            signal: this.abortController.signal,
        });
        if (!stillLive())
            return;
        if (!probeResp.ok && probeResp.status !== 206 && probeResp.status !== 200) {
            throw new Error(`HTTP ${probeResp.status}`);
        }
        const cr = probeResp.headers.get("Content-Range");
        if (cr) {
            const m = cr.match(/\/(\d+)$/);
            if (m)
                this.totalSize = parseInt(m[1], 10);
        }
        let head = new Uint8Array(await probeResp.arrayBuffer());
        if (!stillLive())
            return;
        // If the probe came back as 200, the server doesn't speak Range — the
        // head buffer IS the full file. Set totalSize so the rest of load()
        // sees a consistent picture (avoids an open-ended Range fetch later
        // that would also return 200 and trip B1, even though the bytes are
        // already in memory). Phase A faststart files served by S3/Cloudfront
        // always 206; this guard covers misconfigured nginx caches.
        if (probeResp.status === 200) {
            this.totalSize = head.byteLength;
        }
        this.loadState = "head-fetched";
        // First box scan: does the file have a moov in the head? Or a moof
        // (fragmented MP4), or just mdat (non-frag with moov-at-end)?
        let info = this.findBox(head, "moov");
        if (info.start === -1 || !info.complete) {
            // Look for moof — fragmented (empty moov + per-fragment metadata).
            const moofInfo = this.findBox(head, "moof");
            if (moofInfo.start !== -1) {
                this.isFragmented = true;
                // Fragmented: moov is at file start (empty), moof follows. We need
                // moov to be complete first.
                const completed = await this.completeHeadForBox(head, "moov");
                head = completed.head;
                info = { start: completed.start, end: completed.end, complete: true };
                this.parseMoov(head, info.start, info.end);
                this.moovEnd = info.end;
                this.bodyOffset = info.end;
                this.parseCursor = info.end;
            }
            else {
                // Non-fragmented with moov AFTER mdat — fetch tail.
                this.isFragmented = false;
                if (!this.totalSize) {
                    this.loadState = "error";
                    throw new Error("non-fragmented MP4 with no Content-Length: cannot find moov");
                }
                const tailRange = await this.fetchTailMoov();
                if (!stillLive())
                    return;
                head = tailRange.head;
                info = tailRange.info;
                this.parseMoov(head, info.start, info.end);
                // For non-frag, body extends across the whole file; fetchEntireFile
                // below will populate bodyBytes at offset 0.
                this.moovEnd = info.end;
                this.bodyOffset = 0;
                this.parseCursor = 0;
                if (!this.videoTrack) {
                    this.loadState = "error";
                    throw new Error("no video track in non-frag MP4");
                }
                // NOTE: do NOT set loadComplete=true here — bytes aren't loaded yet.
                // fetchEntireFile() sets it after the body bytes are present.
            }
        }
        else {
            // moov in head — could be fragmented or short non-frag where moov is at start.
            this.parseMoov(head, info.start, info.end);
            this.moovEnd = info.end;
            this.bodyOffset = info.end;
            this.parseCursor = info.end;
            // If we found a moof box in the body, switch to fragmented mode.
            if (this.findBox(head, "moof").start !== -1) {
                this.isFragmented = true;
            }
            else if (this.videoSamples.length > 0) {
                // Sample table came from stbl in moov — non-fragmented short layout.
                this.isFragmented = false;
            }
        }
        // Three load shapes:
        //   1. Fragmented (moov-at-head, mdat in per-fragment moof blocks): stream
        //      via Range; parse moof/mdat as bytes arrive; samples accrue.
        //   2. Non-fragmented moov-at-head (Phase A faststart): sample table is
        //      complete after moov parse, but mdat bytes still need streaming.
        //      Same Range stream path as fragmented; updateBuffered uses sample
        //      cursor (B2); SEI walk deferred to body-complete.
        //   3. Non-fragmented moov-at-end (legacy clips, pre-Phase-A): moov is
        //      at the file tail, so streaming is structurally impossible — fall
        //      back to fetchEntireFile. Out of scope for streaming.
        //
        // Distinguishing #2 from #3 here: the moov-at-end branch above set
        // bodyOffset = 0; the moov-at-head branches set bodyOffset = moovEnd.
        const moovAtHead = !this.isFragmented && this.bodyOffset > 0;
        const willStream = this.isFragmented || moovAtHead;
        if (willStream) {
            // Before the head's fragments are parsed, or they would publish the
            // downloaded length as the clip length.
            if (this.isFragmented)
                this.durationSettled = false;
            // Fail fast on clips whose body cannot possibly fit in memory. Without
            // this, allocating the bodyEstimate would either OOM the tab outright
            // or quietly succeed and then trip appendBody's per-chunk ceiling
            // later. Decision [#18] documents the 75 MB / ~5 min pilot ceiling.
            const bodyEstimate = Math.max(0, (this.totalSize || 0) - this.moovEnd);
            if (bodyEstimate > Playback.BODY_BYTES_CEILING) {
                this.loadState = "error";
                throw new Error(`clip body too large for in-memory streaming: ${bodyEstimate} > ${Playback.BODY_BYTES_CEILING}`);
            }
            // Preallocate a growable body buffer covering bytes after moov.
            // appendBody doubles capacity on overflow, capped by BODY_BYTES_CEILING.
            this.bodyBytes = new Uint8Array(bodyEstimate || 4 * 1024 * 1024);
            // Absorb any post-moov bytes already in head.
            if (head.byteLength > this.moovEnd) {
                const tail = head.subarray(this.moovEnd);
                this.appendBody(tail, this.moovEnd);
                this.parseBody(); // no-op for non-frag; processes moof boxes for fragmented
                // updateBuffered here so the streaming cursor (B2) advances for
                // bytes already in the probe head. Without this call, a non-frag
                // stream that hasn't yet received a body chunk reports
                // bufferedMaxSec=0 even though the probe gave us many samples'
                // worth of bytes. parseBody calls updateBuffered for fragmented
                // (no-op redundancy is harmless).
                this.updateBuffered();
            }
            this.loadState = "moov-parsed";
            // B7: if proactive avcC rebuild is needed for this codec+resolution,
            // make sure the first IDR sample's bytes are present before onReady()
            // (which calls rebuildAvcCFromFirstSample). Without this, the moov may
            // span past the probe head and the first sample lives beyond bodyLen,
            // so the rebuild reads zeros and falls through to decoder-error.
            if (moovAtHead)
                await this.ensureFirstSampleBytes();
            if (!stillLive())
                return;
            // A fragmented clip's empty moov carries no duration, so durationSec
            // would otherwise grow fragment by fragment and drag the seek marker
            // backward. Read the real end time off the tail index in parallel with
            // the body stream; failure just keeps the grow-as-we-go fallback.
            if (this.isFragmented) {
                let timer;
                const timeout = new Promise(res => { timer = setTimeout(() => res(0), Playback.DURATION_PROBE_TIMEOUT_MS); });
                void Promise.race([this.probeFragmentedDuration(), timeout]).catch(() => 0).then((sec) => {
                    clearTimeout(timer);
                    if (!stillLive() || this.durationSettled)
                        return;
                    this.durationSettled = true;
                    if (sec > 0)
                        this.durationSec = Math.max(sec, this.bufferedMaxSec);
                    else
                        this.durationEstimated = true;
                    this.updateBuffered(); // applies the estimate and repaints
                    this.maybeStartPendingPlay();
                });
            }
            await this.onReady();
            if (!stillLive())
                return;
            // Stream the rest. For fragmented, parseBody on each chunk extracts
            // moof-derived samples. For moov-at-head non-frag, samples are
            // already known from stbl; streamRest only fills bytes + updates
            // the buffered cursor.
            await this.streamRest();
        }
        else {
            // Moov-at-end non-frag (legacy): fetchEntireFile() assigns bodyBytes
            // directly. No preallocation — that would double peak memory.
            // Decision [#18] keeps single-buffer model; documented 5min/~75MB
            // ceiling for the pilot. Tracked separately from the streaming
            // ceiling because this path is one-shot.
            await this.fetchEntireFile();
            if (!stillLive())
                return;
            this.loadState = "moov-parsed";
            await this.onReady();
            if (!stillLive())
                return;
            this.finalizeBodyComplete();
        }
    }
    findBox(buf, want) {
        let p = 0;
        while (p + 8 <= buf.byteLength) {
            const size = this.readU32(buf, p);
            const type = this.readType(buf, p + 4);
            let boxEnd;
            if (size === 1) {
                if (p + 16 > buf.byteLength)
                    return { start: p, end: p + 16, complete: false };
                boxEnd = p + this.readU64(buf, p + 8);
            }
            else if (size === 0) {
                boxEnd = buf.byteLength;
            }
            else {
                boxEnd = p + size;
            }
            if (type === want) {
                return { start: p, end: boxEnd, complete: boxEnd <= buf.byteLength };
            }
            if (boxEnd <= p)
                return { start: -1, end: -1, complete: false };
            p = boxEnd;
        }
        return { start: -1, end: p + 8, complete: false };
    }
    // For fragmented MP4: keep fetching head bytes until the requested box is complete.
    async completeHeadForBox(initialHead, boxType) {
        let head = initialHead;
        let info = this.findBox(head, boxType);
        while (!info.complete) {
            if (info.end <= head.byteLength)
                throw new Error(`${boxType} not found`);
            // Cap the head buffer at BODY_BYTES_CEILING. A malformed (or
            // attacker-controlled) box header could declare a giant size and
            // make this loop double allocations indefinitely.
            if (info.end > Playback.BODY_BYTES_CEILING) {
                throw new Error(`${boxType} declared size ${info.end} exceeds in-memory ceiling`);
            }
            const upto = Math.min(this.totalSize || info.end, info.end) - 1;
            const more = await fetch(this.fileUrl, {
                headers: { Range: `bytes=${head.byteLength}-${upto}` },
                signal: this.abortController.signal,
            });
            // B1 mirror: server ignored Range and returned 200 with the full file.
            // Without this guard, the concatenation below would graft file-bytes-
            // from-zero onto the legit probe head, and parseMoov would walk
            // garbage. Same shape as streamRest's B1 check at the chunk reader.
            if (more.status === 200 && head.byteLength > 0) {
                throw new Error(`server returned 200 to Range request — cannot complete ${boxType}`);
            }
            if (!more.ok && more.status !== 206) {
                throw new Error(`HTTP ${more.status} completing ${boxType}`);
            }
            const moreBytes = new Uint8Array(await more.arrayBuffer());
            if (moreBytes.byteLength === 0)
                throw new Error(`unable to complete ${boxType} fetch`);
            const combined = new Uint8Array(head.byteLength + moreBytes.byteLength);
            combined.set(head, 0);
            combined.set(moreBytes, head.byteLength);
            head = combined;
            info = this.findBox(head, boxType);
        }
        return { head, start: info.start, end: info.end, complete: true };
    }
    // For non-fragmented MP4 with moov at end: fetch the tail (~1MB), walk box
    // sizes from the tail's absolute offset to locate moov.
    //
    // The previous implementation synthesized a totalSize-byte zero-padded
    // buffer and called findBox from offset 0 — that hit a size-0 box at the
    // zero-padded prefix and bailed before reaching the real moov. Two reviews
    // (Claude adversarial + Codex) flagged this independently. Fixed: walk the
    // tail buffer's actual bytes; the only call site (parseMoov) takes the head
    // buffer + start/end offsets, so we pass the tail with offsets adjusted
    // relative to its byte 0.
    async fetchTailMoov() {
        const TAIL = Math.min(this.totalSize, 1024 * 1024);
        const startAbs = Math.max(0, this.totalSize - TAIL);
        const resp = await fetch(this.fileUrl, {
            headers: { Range: `bytes=${startAbs}-${this.totalSize - 1}` },
            signal: this.abortController.signal,
        });
        if (!resp.ok && resp.status !== 206)
            throw new Error(`HTTP ${resp.status}`);
        const tail = new Uint8Array(await resp.arrayBuffer());
        // Walk box sizes from the END of the tail backwards to find moov.
        // MP4 box headers are at known positions if you walk forward, but
        // we don't know where the tail starts in box-stream terms. So we walk
        // forward from each possible box-boundary candidate.
        //
        // Simpler approach: forward-walk from the first plausible box header
        // (search for 'moov' four-CC at byte boundaries, then validate the box).
        const moovTag = new Uint8Array([0x6d, 0x6f, 0x6f, 0x76]); // 'moov'
        let foundRel = -1;
        for (let i = 0; i + 8 <= tail.byteLength; i++) {
            if (tail[i + 4] === moovTag[0] &&
                tail[i + 5] === moovTag[1] &&
                tail[i + 6] === moovTag[2] &&
                tail[i + 7] === moovTag[3]) {
                // i points at the size field of a 'moov' box. Validate the size.
                const size = this.readU32(tail, i);
                if (size > 8 && i + size <= tail.byteLength) {
                    foundRel = i;
                    break;
                }
                if (size === 1 && i + 16 <= tail.byteLength) {
                    // 64-bit size; trust it if the absolute end lands inside the file
                    const bigSize = this.readU64(tail, i + 8);
                    if (i + bigSize <= tail.byteLength) {
                        foundRel = i;
                        break;
                    }
                }
            }
        }
        if (foundRel === -1) {
            // moov spans across tail-start boundary — fetch the whole file once.
            const fullResp = await fetch(this.fileUrl, { signal: this.abortController.signal });
            const full = new Uint8Array(await fullResp.arrayBuffer());
            const info2 = this.findBox(full, "moov");
            if (info2.start === -1)
                throw new Error("moov not found in file");
            return { head: full, info: info2 };
        }
        const size = this.readU32(tail, foundRel);
        const moovEndRel = size === 1
            ? foundRel + this.readU64(tail, foundRel + 8)
            : foundRel + size;
        return {
            head: tail,
            info: { start: foundRel, end: moovEndRel, complete: true },
        };
    }
    // Fragmented clips (mpengine sRecordLayout=frag) end with an mfra index
    // (ffmpeg writes it on a clean close) whose mfro trailer gives its size.
    // The last tfra entry points at the last moof; its tfdt plus the trun
    // sample durations is the clip's end time. Three small Range reads, none
    // touching mdat. Returns 0 when the clip has no index (crash-truncated
    // recording, server without Range) so the caller keeps its fallback.
    async probeFragmentedDuration() {
        const track = this.videoTrack;
        if (!track || !track.timescale || this.totalSize < 16)
            return 0;
        const tail = await this.tailFetch;
        const range = async (from, to) => {
            if (tail && tail.total === this.totalSize && from >= tail.start) {
                return tail.bytes.subarray(from - tail.start, to - tail.start);
            }
            const resp = await fetch(this.fileUrl, {
                headers: { Range: `bytes=${from}-${to - 1}` },
                signal: this.abortController.signal,
            });
            if (resp.status !== 206)
                throw new Error(`HTTP ${resp.status}`);
            return new Uint8Array(await resp.arrayBuffer());
        };
        const mfro = await range(this.totalSize - 16, this.totalSize);
        if (mfro.byteLength !== 16 || this.readType(mfro, 4) !== "mfro")
            return 0;
        const mfraSize = this.readU32(mfro, 12);
        if (mfraSize < 16 || mfraSize > this.totalSize)
            return 0;
        const mfra = await range(this.totalSize - mfraSize, this.totalSize);
        if (this.readType(mfra, 4) !== "mfra")
            return 0;
        let lastMoof = -1;
        this.walkBoxes(mfra, 8, mfra.byteLength, (t, s, e, h) => {
            if (t !== "tfra")
                return;
            const version = mfra[s + h];
            let p = s + h + 4;
            const trackID = this.readU32(mfra, p);
            p += 4;
            if (trackID !== track.id)
                return;
            const lengths = this.readU32(mfra, p);
            p += 4;
            const count = this.readU32(mfra, p);
            p += 4;
            const entryLen = (version === 1 ? 16 : 8)
                + ((lengths >> 4) & 3) + 1 + ((lengths >> 2) & 3) + 1 + (lengths & 3) + 1;
            if (count === 0)
                return;
            const last = p + (count - 1) * entryLen;
            if (last + entryLen > e)
                return; // truncated index: keep the fallback
            lastMoof = version === 1 ? this.readU64(mfra, last + 8) : this.readU32(mfra, last + 4);
        });
        if (lastMoof < 0 || lastMoof >= this.totalSize)
            return 0;
        let moofHead = await range(lastMoof, Math.min(this.totalSize, lastMoof + 65536));
        if (moofHead.byteLength < 8 || this.readType(moofHead, 4) !== "moof")
            return 0;
        let moofSize = this.readU32(moofHead, 0);
        let moofHdr = 8;
        if (moofSize === 1 && moofHead.byteLength >= 16) {
            moofSize = this.readU64(moofHead, 8);
            moofHdr = 16;
        }
        if (moofSize < moofHdr || lastMoof + moofSize > this.totalSize)
            return 0;
        // A long fragment's moof can outgrow the first read; fetch it whole.
        if (moofSize > moofHead.byteLength)
            moofHead = await range(lastMoof, lastMoof + moofSize);
        const moofEnd = moofSize;
        let endTicks = 0;
        this.walkBoxes(moofHead, moofHdr, moofEnd, (t, s, e, h) => {
            if (t !== "traf")
                return;
            const { tfhd, tfdt, truns } = this.readTraf(moofHead, s + h, e);
            if (!tfhd || tfhd.trackID !== track.id)
                return;
            // Presentation end, matching bufferedMaxSec (cts + duration): with
            // B-frames the last shown frame can sit past the last decode time.
            let dts = tfdt;
            for (const trun of truns) {
                for (const smp of trun.samples) {
                    const dur = smp.duration ?? tfhd.defaultSampleDuration;
                    endTicks = Math.max(endTicks, dts + (smp.ctsOffset ?? 0) + dur);
                    dts += dur;
                }
            }
        });
        return endTicks / track.timescale;
    }
    // B7: if this codec+resolution has previously needed an avcC rebuild,
    // make sure the FIRST sample's bytes are present in bodyBytes before
    // onReady() configures the decoder (onReady calls rebuildAvcCFromFirstSample
    // which slices the first sample). For Phase A faststart files where moov
    // is at the head, the first sample lives at moovEnd, so a single small
    // targeted Range fetch covers it. Without this, rebuild silently reads
    // zeros and falls through to the slow decoder-error retry path.
    async ensureFirstSampleBytes() {
        if (!this.videoTrack || this.videoSamples.length === 0)
            return;
        if (!this.videoTrack.codec)
            return;
        const key = Playback.cacheKey(this.videoTrack.codec, this.videoTrack.width, this.videoTrack.height);
        if (!Playback.requiresRebuiltAvcC.has(key))
            return;
        const first = this.videoSamples[0];
        const firstEnd = first.byteOffset + first.size;
        const bodyEnd = this.bodyOffset + this.bodyLen;
        if (firstEnd <= bodyEnd)
            return; // already present
        await this.fetchSampleRange(first.byteOffset, firstEnd);
    }
    // Targeted Range fetch for a single sample-byte interval. Caller is
    // responsible for state — used to seed bodyBytes before onReady (B7)
    // and is a future-extensibility hook for sample-on-demand seek (not
    // yet wired). Uses appendBody at the absolute offset, so the body
    // buffer must already be allocated with bodyOffset set.
    async fetchSampleRange(startAbs, endAbs) {
        if (endAbs <= startAbs)
            return;
        const resp = await fetch(this.fileUrl, {
            headers: { Range: `bytes=${startAbs}-${endAbs - 1}` },
            signal: this.abortController.signal,
        });
        if (resp.status === 200) {
            // Server ignored Range. We could land bytes from offset 0 here, but
            // the appendBody rel-offset math (rel < 0) would silently drop them.
            // Surface as an error rather than continue with an unprimed buffer.
            this.loadState = "error";
            throw new Error("server ignored Range on targeted sample fetch");
        }
        if (!resp.ok && resp.status !== 206) {
            this.loadState = "error";
            throw new Error(`HTTP ${resp.status} on targeted sample fetch`);
        }
        const bytes = new Uint8Array(await resp.arrayBuffer());
        if (bytes.byteLength === 0)
            return;
        // Short-body guard. RFC 7233 lets a server return 206 with a body
        // shorter than the requested range (e.g. EOF mid-range, server-side
        // throttling). Without this check, appendBody writes the prefix and
        // bodyLen advances less than firstEnd, so onReady's downstream
        // rebuildAvcCFromFirstSample reads zero-init bytes past the prefix
        // and configures the decoder with garbage. Refuse rather than ship a
        // broken decoder config that would also poison the requiresRebuiltAvcC
        // cache for every subsequent clip of the same camera.
        const wantedLen = endAbs - startAbs;
        if (bytes.byteLength < wantedLen) {
            this.loadState = "error";
            throw new Error(`targeted sample fetch returned short body: got ${bytes.byteLength}, expected ${wantedLen}`);
        }
        this.appendBody(bytes, startAbs);
    }
    async fetchEntireFile() {
        const resp = await fetch(this.fileUrl, { signal: this.abortController.signal });
        if (!resp.ok)
            throw new Error(`HTTP ${resp.status}`);
        // Pre-allocation ceiling check (B6 parity with the streaming path).
        // Without this, a moov-at-end legacy clip larger than the in-memory
        // ceiling would OOM the tab on `await arrayBuffer()`. Use Content-Length
        // if available; fall back to totalSize from the probe; if neither is
        // present, let the read proceed (cheaper to fail late than to refuse
        // legitimate clips on a tiny embedded server with no headers).
        const advertisedLen = parseInt(resp.headers.get("Content-Length") ?? "", 10);
        const expectedSize = isFinite(advertisedLen) ? advertisedLen : this.totalSize;
        if (expectedSize > Playback.BODY_BYTES_CEILING) {
            this.loadState = "error";
            throw new Error(`clip too large for in-memory load: ${expectedSize} > ${Playback.BODY_BYTES_CEILING}`);
        }
        const bytes = new Uint8Array(await resp.arrayBuffer());
        // Post-read enforcement in case headers lied (or were absent).
        if (bytes.byteLength > Playback.BODY_BYTES_CEILING) {
            this.loadState = "error";
            throw new Error(`clip body exceeded in-memory ceiling: ${bytes.byteLength} > ${Playback.BODY_BYTES_CEILING}`);
        }
        if (this.bodyBytes && bytes.length <= this.bodyBytes.byteLength) {
            this.bodyBytes.set(bytes, 0);
            this.bodyLen = bytes.length;
        }
        else {
            this.bodyBytes = bytes;
            this.bodyLen = bytes.length;
        }
        this.bodyOffset = 0;
        this.totalSize = bytes.length;
        this.loadComplete = true;
        this.updateBuffered();
    }
    async streamRest() {
        const startByte = this.bodyOffset + this.bodyLen;
        if (this.totalSize && startByte >= this.totalSize) {
            // Everything was already in the head buffer.
            this.finalizeBodyComplete();
            return;
        }
        const resp = await fetch(this.fileUrl, {
            headers: { Range: `bytes=${startByte}-` },
            signal: this.abortController.signal,
        });
        // B1: a server that doesn't support Range silently returns 200 + the
        // full body from offset 0. Those bytes would land at bodyBytes[startByte]
        // and corrupt every sample byteOffset that points into mdat. Reject
        // outright so the React shell can swap to <video> fallback.
        if (resp.status === 200 && startByte > 0) {
            this.loadState = "error";
            throw new Error("server returned 200 to Range request — Range not supported, cannot stream");
        }
        if (!resp.ok && resp.status !== 206) {
            this.loadState = "error";
            throw new Error(`HTTP ${resp.status}`);
        }
        if (!resp.body) {
            this.finalizeBodyComplete();
            return;
        }
        this.loadState = "streaming-body";
        const reader = resp.body.getReader();
        let fileOffset = startByte;
        try {
            while (true) {
                const { value, done } = await reader.read();
                if (done)
                    break;
                if (this.destroyed)
                    return;
                this.appendBody(value, fileOffset);
                fileOffset += value.byteLength;
                this.parseBody(); // no-op for non-frag, parses moof for fragmented
                this.updateBuffered();
                this.maybeStartPendingPlay();
            }
        }
        catch (e) {
            const name = e.name;
            if (name === "AbortError")
                return;
            // If appendBody (or another sync helper inside the loop) threw, it
            // already set loadState = "error". Re-raise so load()'s upstream
            // catch in the constructor fires onError() and the shell falls
            // back to <video>.
            // (Cast through unknown because TS narrowed loadState to
            // "streaming-body" via the assignment above and doesn't see the
            // possibility that appendBody mutated it.)
            if (this.loadState === "error") {
                throw e;
            }
            // B8: mid-stream network failure. Treat as fatal: clear the
            // first-frame watchdog (so it doesn't double-fire 10s later),
            // flip to error state, and call onError() with no reason so the
            // React shell falls back to <video> (which will retry the URL on
            // the native path).
            //
            // The earlier "preserve painted canvas frames + show overlay"
            // design required a shell-side overlay element + load-state
            // coordination that's out of scope for Phase B (DragonPlayer
            // doesn't pass an overlay element today, and onCanvasError
            // unconditionally swaps to <video> regardless of reason). The
            // pragmatic call is to mirror what fetchEntireFile already did
            // on net errors — fall through to the proven <video> fallback.
            if (this.firstFrameWatchdog !== null) {
                clearTimeout(this.firstFrameWatchdog);
                this.firstFrameWatchdog = null;
            }
            this.loadState = "error";
            this.setOverlay("Network error");
            try {
                this.runtimeOpts.onError?.();
            }
            catch { /* noop */ }
            return;
        }
        // B4: short-stream guard. If reader.read() returned done before we
        // reached totalSize the server truncated us. videoSamples still
        // references byteOffsets we never received; sliceSample() will return
        // null for them and feedVideo will silently drop frames. Surface it.
        if (this.totalSize && fileOffset !== this.totalSize) {
            this.loadState = "error";
            throw new Error(`stream truncated: received ${fileOffset} of ${this.totalSize} bytes`);
        }
        this.finalizeBodyComplete();
    }
    // Common transition for "all body bytes are now in bodyBytes." Reached via
    // streamRest completion, the head-already-had-everything shortcut, and the
    // fetchEntireFile path (set explicitly there). Does NOT fire for the
    // streaming error path.
    finalizeBodyComplete() {
        this.loadState = "body-complete";
        this.loadComplete = true;
        this.parseBody();
        this.updateBuffered();
        // Every fragment is in: the last sample's end is the authoritative length,
        // superseding the tail-index probe if the two disagree by a frame.
        this.durationSettled = true;
        if (this.isFragmented && this.bufferedMaxSec > 0)
            this.durationSec = this.bufferedMaxSec;
        this.updateUi();
        // SEI walk: deferred until body-complete so byteOffsets in stbl don't
        // walk past unloaded bytes. Already a no-op for fragmented files
        // (cacheSeiForSample fired per-trun during parseMoof).
        if (!this.isFragmented)
            this.extractAllSeiNonFrag();
        this.maybeStartPendingPlay();
    }
    // ---- moov parsing -------------------------------------------------------
    parseMoov(buf, start, end) {
        this.walkBoxes(buf, start + 8, end, (t, s, e, h) => {
            if (t === "mvhd") {
                const version = buf[s + h];
                const o = s + h + 4;
                let timescale, duration;
                if (version === 1) {
                    timescale = this.readU32(buf, o + 16);
                    duration = this.readU64(buf, o + 20);
                }
                else {
                    timescale = this.readU32(buf, o + 8);
                    duration = this.readU32(buf, o + 12);
                }
                void timescale; // timescale captured into track.timescale via mdhd below
                this.durationSec = timescale ? duration / timescale : 0;
            }
            else if (t === "trak") {
                this.parseTrak(buf, s + h, e);
            }
        });
    }
    parseTrak(buf, start, end) {
        const track = {
            id: 0, codec: null, codecKind: null, description: null,
            timescale: 0, width: 0, height: 0, handler: "", handlerName: "",
        };
        let stblStart = -1, stblEnd = -1, stblHdr = 0;
        this.walkBoxes(buf, start, end, (t, s, e, h) => {
            if (t === "tkhd") {
                const version = buf[s + h];
                const o = s + h + 4;
                track.id = version === 1 ? this.readU32(buf, o + 16) : this.readU32(buf, o + 8);
            }
            else if (t === "mdia") {
                this.walkBoxes(buf, s + h, e, (t2, s2, e2, h2) => {
                    if (t2 === "mdhd") {
                        const version = buf[s2 + h2];
                        const o = s2 + h2 + 4;
                        track.timescale = version === 1 ? this.readU32(buf, o + 16) : this.readU32(buf, o + 8);
                    }
                    else if (t2 === "hdlr") {
                        const o = s2 + h2 + 4;
                        track.handler = this.readType(buf, o + 4);
                        const nameStart = o + 20;
                        let nameEnd = nameStart;
                        while (nameEnd < e2 && buf[nameEnd] !== 0)
                            nameEnd++;
                        track.handlerName = new TextDecoder().decode(buf.subarray(nameStart, nameEnd));
                    }
                    else if (t2 === "minf") {
                        this.walkBoxes(buf, s2 + h2, e2, (t3, s3, e3, h3) => {
                            if (t3 !== "stbl")
                                return;
                            stblStart = s3 + h3;
                            stblEnd = e3;
                            stblHdr = h3;
                            this.walkBoxes(buf, s3 + h3, e3, (t4, s4, e4, h4) => {
                                if (t4 === "stsd")
                                    this.parseStsd(buf, s4 + h4, e4, track);
                            });
                        });
                    }
                });
            }
        });
        void stblHdr;
        if (track.handler === "vide") {
            this.videoTrack = track;
            // For non-fragmented MP4, walk the full stbl now and reconstruct samples.
            if (stblStart !== -1 && !this.isFragmented) {
                this.parseStblSamples(buf, stblStart, stblEnd);
            }
        }
    }
    parseStsd(buf, start, end, track) {
        let p = start + 8;
        if (p + 8 > end)
            return;
        const entrySize = this.readU32(buf, p);
        const entryType = this.readType(buf, p + 4);
        const entryEnd = p + entrySize;
        if (entryEnd > end)
            return;
        if (entryType === "avc1" || entryType === "avc3" ||
            entryType === "hev1" || entryType === "hvc1") {
            track.width = this.readU16(buf, p + 32);
            track.height = this.readU16(buf, p + 34);
            const childrenStart = p + 86;
            this.walkBoxes(buf, childrenStart, entryEnd, (t, s, e, h) => {
                if (t === "avcC") {
                    const desc = buf.subarray(s + h, e).slice();
                    track.description = desc;
                    track.codec = "avc1." + [desc[1], desc[2], desc[3]]
                        .map(b => b.toString(16).padStart(2, "0").toUpperCase()).join("");
                    track.codecKind = "h264";
                }
                else if (t === "hvcC") {
                    const desc = buf.subarray(s + h, e).slice();
                    track.description = desc;
                    track.codec = this.makeHevcCodecString(entryType, desc);
                    track.codecKind = "hevc";
                }
            });
        }
    }
    makeHevcCodecString(fourcc, desc) {
        const profileByte = desc[1];
        const profileSpace = (profileByte >> 6) & 0x3;
        const tierFlag = (profileByte >> 5) & 0x1;
        const profileIdc = profileByte & 0x1F;
        const compatU32 = this.readU32(desc, 2);
        let reversed = 0;
        for (let i = 0; i < 32; i++)
            if (compatU32 & (1 << i))
                reversed |= (1 << (31 - i));
        reversed = reversed >>> 0;
        const levelIdc = desc[12];
        const constraintBytes = [];
        for (let i = 6; i <= 11; i++)
            constraintBytes.push(desc[i].toString(16).padStart(2, "0").toUpperCase());
        while (constraintBytes.length > 1 && constraintBytes[constraintBytes.length - 1] === "00")
            constraintBytes.pop();
        const spacePrefix = profileSpace === 0 ? ""
            : String.fromCharCode("A".charCodeAt(0) + profileSpace - 1);
        return `${fourcc}.${spacePrefix}${profileIdc}.${reversed.toString(16).toUpperCase()}.${tierFlag ? "H" : "L"}${levelIdc}.${constraintBytes.join(".")}`;
    }
    // Non-fragmented: walk stbl sub-boxes and reconstruct per-sample records
    // (decision [#2]). MUST read ctts for correct B-frame mapping (decision [#20]).
    parseStblSamples(buf, start, end) {
        let sttsEntries = [];
        let cttsEntries = [];
        let stscEntries = [];
        let stcoOffsets = [];
        let stszSize = 0;
        let stszSizes = [];
        const stssIndices = new Set();
        this.walkBoxes(buf, start, end, (t, s, _e, h) => {
            const dataStart = s + h + 4; // skip version/flags
            if (t === "stts") {
                const count = this.readU32(buf, dataStart);
                for (let i = 0; i < count; i++) {
                    const sampleCount = this.readU32(buf, dataStart + 4 + i * 8);
                    const delta = this.readU32(buf, dataStart + 8 + i * 8);
                    sttsEntries.push({ count: sampleCount, delta });
                }
            }
            else if (t === "ctts") {
                // ctts version: 0=u32 offsets, 1=s32 offsets (B-frames)
                const version = buf[s + h];
                const count = this.readU32(buf, dataStart);
                for (let i = 0; i < count; i++) {
                    const sampleCount = this.readU32(buf, dataStart + 4 + i * 8);
                    const offsetRaw = this.readU32(buf, dataStart + 8 + i * 8);
                    const offset = version === 1 ? (offsetRaw | 0) : offsetRaw;
                    cttsEntries.push({ count: sampleCount, offset });
                }
            }
            else if (t === "stsc") {
                const count = this.readU32(buf, dataStart);
                for (let i = 0; i < count; i++) {
                    const firstChunk = this.readU32(buf, dataStart + 4 + i * 12);
                    const samplesPerChunk = this.readU32(buf, dataStart + 8 + i * 12);
                    const descIndex = this.readU32(buf, dataStart + 12 + i * 12);
                    stscEntries.push({ firstChunk, samplesPerChunk, descIndex });
                }
            }
            else if (t === "stco") {
                const count = this.readU32(buf, dataStart);
                for (let i = 0; i < count; i++) {
                    stcoOffsets.push(this.readU32(buf, dataStart + 4 + i * 4));
                }
            }
            else if (t === "co64") {
                const count = this.readU32(buf, dataStart);
                for (let i = 0; i < count; i++) {
                    stcoOffsets.push(this.readU64(buf, dataStart + 4 + i * 8));
                }
            }
            else if (t === "stsz") {
                stszSize = this.readU32(buf, dataStart);
                const count = this.readU32(buf, dataStart + 4);
                if (stszSize === 0) {
                    for (let i = 0; i < count; i++)
                        stszSizes.push(this.readU32(buf, dataStart + 8 + i * 4));
                }
            }
            else if (t === "stss") {
                const count = this.readU32(buf, dataStart);
                for (let i = 0; i < count; i++)
                    stssIndices.add(this.readU32(buf, dataStart + 4 + i * 4));
            }
        });
        // Build per-sample byte offsets by walking stsc chunks against stco offsets.
        const chunkSampleCounts = new Array(stcoOffsets.length).fill(0);
        if (stscEntries.length === 0 || stcoOffsets.length === 0)
            return;
        for (let chunkIdx = 0; chunkIdx < stcoOffsets.length; chunkIdx++) {
            const chunkNum = chunkIdx + 1;
            // Find the stsc entry that applies to this chunk: the latest entry with firstChunk <= chunkNum.
            let applies = stscEntries[0].samplesPerChunk;
            for (const entry of stscEntries) {
                if (entry.firstChunk <= chunkNum)
                    applies = entry.samplesPerChunk;
                else
                    break;
            }
            // Reject malformed stsc: samples_per_chunk MUST be > 0 (a chunk with
            // zero samples is structurally meaningless). If we let it through, the
            // sample iteration loop below silently skips chunks and the resulting
            // sample table is misaligned against the actual byte stream.
            if (applies <= 0) {
                // eslint-disable-next-line no-console
                console.warn("[playback] stsc samples_per_chunk=0 for chunk", chunkNum, "— aborting sample reconstruction");
                return;
            }
            chunkSampleCounts[chunkIdx] = applies;
        }
        // Decode time per sample from stts run-length deltas.
        const dtsPerSample = [];
        {
            let dts = 0;
            for (const e of sttsEntries) {
                for (let i = 0; i < e.count; i++) {
                    dtsPerSample.push(dts);
                    dts += e.delta;
                }
            }
        }
        // cts offset per sample from ctts run-length entries; default 0.
        const ctsOffsetPerSample = new Array(dtsPerSample.length).fill(0);
        {
            let idx = 0;
            for (const e of cttsEntries) {
                for (let i = 0; i < e.count && idx < ctsOffsetPerSample.length; i++) {
                    ctsOffsetPerSample[idx] = e.offset;
                    idx++;
                }
            }
        }
        // Build sample records by walking chunks and reading byteOffset, size from stsz.
        let sampleIdx = 0;
        let chunkByteCursor = 0;
        for (let chunkIdx = 0; chunkIdx < stcoOffsets.length; chunkIdx++) {
            chunkByteCursor = stcoOffsets[chunkIdx];
            const samplesInChunk = chunkSampleCounts[chunkIdx];
            for (let i = 0; i < samplesInChunk && sampleIdx < dtsPerSample.length; i++) {
                const size = stszSize !== 0 ? stszSize : (stszSizes[sampleIdx] ?? 0);
                const dts = dtsPerSample[sampleIdx];
                const cts = dts + ctsOffsetPerSample[sampleIdx];
                // stss (when present) lists sync-sample indices (1-based). When stss
                // is absent every sample is a sync sample.
                const isKey = stssIndices.size === 0 || stssIndices.has(sampleIdx + 1);
                // duration: read next sample's dts; for the last sample, use last delta.
                const nextDts = sampleIdx + 1 < dtsPerSample.length
                    ? dtsPerSample[sampleIdx + 1]
                    : (dts + (sttsEntries.length > 0 ? sttsEntries[sttsEntries.length - 1].delta : 0));
                const duration = Math.max(0, nextDts - dts);
                this.videoSamples.push({ byteOffset: chunkByteCursor, size, dts, cts, duration, isKey });
                chunkByteCursor += size;
                sampleIdx++;
            }
        }
    }
    // Ordered list of hardwareAcceleration hints isConfigSupported claimed work,
    // and a cursor pointing at the one currently in use. probeSupportedConfig
    // fills this during onReady() with the supported subset of
    // ["prefer-software", "no-preference", "prefer-hardware"].
    //
    // isConfigSupported() can lie at high resolutions — Chromium returns
    // supported:true for prefer-software at 3072x1728 avc1.640032, then
    // configure()/decode() rejects with EncodingError. On decoder error or a
    // 3s no-frame stall we advance the cursor and rebuild the decoder with the
    // next hint via tryNextHwAccel(); only after exhausting the list do we
    // surface onError() to the React shell so it swaps to native <video>.
    hwAccelOptions = ["prefer-software"];
    // True after we've replaced videoTrack.description with one rebuilt from
    // the in-band SPS/PPS in the first sample. Gates the one-shot retry path
    // in tryNextHwAccel — we don't loop rebuilding forever.
    rebuiltAvcCAttempted = false;
    hwAccelIdx = 0;
    get hwAccel() {
        return this.hwAccelOptions[this.hwAccelIdx] ?? "prefer-software";
    }
    async onReady() {
        if (!this.videoTrack)
            throw new Error("no video track found");
        if (!this.videoTrack.codec || !this.videoTrack.description) {
            throw new Error("video codec config missing");
        }
        // If this codec+resolution has previously needed an avcC rebuild (mp4
        // avcC SPS doesn't match in-band SPS — observed on cs2347 cam2's
        // 3072x1728 clips), apply the rebuild PROACTIVELY before configuring
        // the decoder. Saves the user the decoder-error wait on every clip.
        if (this.videoTrack.codec) {
            const key = Playback.cacheKey(this.videoTrack.codec, this.videoTrack.width, this.videoTrack.height);
            if (Playback.requiresRebuiltAvcC.has(key)) {
                const rebuilt = this.rebuildAvcCFromFirstSample();
                if (rebuilt) {
                    this.videoTrack.description = rebuilt;
                    this.rebuiltAvcCAttempted = true;
                }
            }
        }
        const supported = await this.probeSupportedConfig();
        if (this.destroyed)
            return;
        if (supported.length === 0) {
            // No hardwareAcceleration hint produced a supported config for this
            // clip on this browser. Hand off to the <video> fallback path; the
            // shell will swap the canvas for a native element with the same src.
            this.runtimeOpts.onError?.();
            return;
        }
        // Last-known-good hint, if any: reorder the supported list so the
        // cached hint goes first. cs2347's first clip after page-load eats one
        // decoder-error fallthrough to discover "prefer-software lies for
        // 3072×1728"; clips 2..N for the same codec+resolution skip the wait.
        this.hwAccelOptions = this.reorderByCachedGood(supported);
        this.hwAccelIdx = 0;
        this.decoder = this.makeDecoder();
        if (this.seekEL)
            this.seekEL.max = "1000";
        this.updateUi();
        this.play();
    }
    // Probe each hardwareAcceleration hint in priority order. prefer-software
    // first preserves the historical Chrome/Safari path (and dodges Chrome HW
    // decoder VFR refusals on cam4-style clips). no-preference lets Chromium
    // forks without a software H.264 decoder still play via hardware. The
    // probe runs the FULL config (codec + description + dimensions + hwAccel),
    // so it catches avcC-blob rejections too — not just hint mismatches.
    //
    // Returns ALL supported hints in priority order so the runtime can
    // fall through on async decoder failures (isConfigSupported lies for
    // 3072x1728 prefer-software in Chromium). Empty array = no path forward;
    // caller falls straight to <video>.
    async probeSupportedConfig() {
        if (!this.videoTrack || !this.videoTrack.codec || !this.videoTrack.description)
            return [];
        const base = {
            codec: this.videoTrack.codec,
            description: this.videoTrack.description,
            codedWidth: this.videoTrack.width,
            codedHeight: this.videoTrack.height,
        };
        const hints = ["prefer-software", "no-preference", "prefer-hardware"];
        const supported = [];
        for (const hardwareAcceleration of hints) {
            try {
                const result = await VideoDecoder.isConfigSupported({ ...base, hardwareAcceleration });
                if (result.supported)
                    supported.push(hardwareAcceleration);
            }
            catch (e) {
                // isConfigSupported throws on malformed config (rare; would mean
                // the avcC blob is unparseable). Keep trying other hints; if all
                // fail we'll surface the diagnostic below.
                // eslint-disable-next-line no-console
                console.warn("[playback] isConfigSupported threw for hint", hardwareAcceleration, e);
            }
        }
        if (supported.length === 0) {
            this.logRejectedConfig("no supported VideoDecoder config — falling back");
        }
        return supported;
    }
    // If a hint is cached for this clip's codec+resolution, move it to the
    // front of the supported list. Otherwise leave the original priority
    // order intact. Cache lookup is fingerprinted on codec+width×height —
    // description bytes are identical across the camera's clips so we don't
    // need to include them.
    reorderByCachedGood(supported) {
        if (!this.videoTrack || !this.videoTrack.codec)
            return supported;
        const key = Playback.cacheKey(this.videoTrack.codec, this.videoTrack.width, this.videoTrack.height);
        const cached = Playback.hwAccelGood.get(key);
        if (!cached)
            return supported;
        if (!supported.includes(cached))
            return supported;
        // Move cached to head; keep original order for the rest as fallback.
        const rest = supported.filter(h => h !== cached);
        return [cached, ...rest];
    }
    // Advance to the next hardwareAcceleration hint after a runtime failure
    // (decoder error event OR 3s stall watchdog). Closes the old decoder,
    // creates a new one configured with the next hint, and re-primes the feed
    // from the keyframe before the current playhead so the new decoder gets
    // a valid GOP start. Returns true if a retry was scheduled, false if all
    // hints have been exhausted and the caller should fall back to <video>.
    //
    // When hwAccel options are exhausted, attempts one more retry by rebuilding
    // videoTrack.description from the in-band SPS/PPS in the first sample —
    // catches the cs2347 case where mp4's avcC SPS disagrees with the slice
    // data's referenced SPS. Once rebuilt, the hwAccelIdx cursor resets and
    // the full hint sequence is retried in the new mode.
    tryNextHwAccel(reason) {
        if (this.destroyed)
            return false;
        const next = this.hwAccelIdx + 1;
        if (next >= this.hwAccelOptions.length) {
            // hwAccel options exhausted — try rebuilding avcC from in-band SPS/PPS.
            return this.tryRebuildAvcC(reason);
        }
        const from = this.hwAccelOptions[this.hwAccelIdx];
        const to = this.hwAccelOptions[next];
        this.hwAccelIdx = next;
        // eslint-disable-next-line no-console
        console.warn(`[playback] hwAccel fallthrough ${from} → ${to} after ${reason}`);
        this.resetAndReconfigure();
        // Re-prime the feed from the keyframe at/before current playhead so the
        // new decoder receives a valid GOP start. play()/seek() normally handle
        // this, but the error fires async so we must rewind here ourselves.
        if (this.videoTrack && !this.destroyed) {
            const ts = this.playheadSec * this.videoTrack.timescale;
            this.nextVideoIdx = this.findKeyframeIdx(ts);
            this.feedVideo(this.playheadSec);
        }
        return true;
    }
    // One-shot retry: replace videoTrack.description with an avcC blob built
    // from the in-band SPS/PPS in the first video sample. The mp4 avcC can
    // disagree with what the slice data references (cs2347 cam2 case —
    // num_ref_frames=4 declared vs 1 used). Rebuilding fixes the SPS
    // mismatch, lets the decoder configure correctly, and resets the hwAccel
    // cursor so the full hint sequence retries against the new description.
    // Returns true if rebuild succeeded and a retry was scheduled.
    tryRebuildAvcC(reason) {
        if (this.destroyed || this.rebuiltAvcCAttempted)
            return false;
        if (!this.videoTrack)
            return false;
        const rebuilt = this.rebuildAvcCFromFirstSample();
        if (!rebuilt)
            return false;
        this.rebuiltAvcCAttempted = true;
        // eslint-disable-next-line no-console
        console.warn(`[playback] rebuilding avcC from in-band SPS/PPS after ${reason} (mp4 avcC mismatch)`);
        this.videoTrack.description = rebuilt;
        // Reset cursor so the full hint sequence retries against the new avcC.
        this.hwAccelIdx = 0;
        this.resetAndReconfigure();
        if (this.videoTrack && !this.destroyed) {
            const ts = this.playheadSec * this.videoTrack.timescale;
            this.nextVideoIdx = this.findKeyframeIdx(ts);
            this.feedVideo(this.playheadSec);
        }
        return true;
    }
    // Scan the first video sample for SPS (NAL type 7) and PPS (NAL type 8)
    // NALs and rebuild a fresh avcC blob using those, plus the profile/level
    // bytes from the SPS itself. The rebuilt blob keeps lengthSizeMinusOne=3
    // (4-byte length prefix) to match what the player feeds. Returns null if
    // the first sample is unavailable or doesn't contain SPS+PPS.
    rebuildAvcCFromFirstSample() {
        if (this.videoSamples.length === 0)
            return null;
        const first = this.videoSamples[0];
        const bytes = this.sliceSample(first);
        if (!bytes)
            return null;
        return this.buildAvcCFromNalBytes(bytes);
    }
    // Visible-for-test helper: parse a length-prefixed NAL byte buffer for
    // the first H.264 SPS + PPS and assemble an avcC. Pure function — no
    // state dependencies — so unit tests can synthesize input.
    buildAvcCFromNalBytes(bytes) {
        let sps = null;
        let pps = null;
        let p = 0;
        while (p + 4 <= bytes.byteLength) {
            const len = this.readU32(bytes, p);
            if (len === 0 || p + 4 + len > bytes.byteLength)
                break;
            const t = bytes[p + 4] & 0x1F;
            if (t === 7 && !sps)
                sps = bytes.subarray(p + 4, p + 4 + len);
            else if (t === 8 && !pps)
                pps = bytes.subarray(p + 4, p + 4 + len);
            if (sps && pps)
                break;
            p += 4 + len;
        }
        if (!sps || !pps || sps.byteLength < 4)
            return null;
        const total = 1 + 1 + 1 + 1 + 1 + 1 + 2 + sps.byteLength + 1 + 2 + pps.byteLength;
        const avcC = new Uint8Array(total);
        let i = 0;
        avcC[i++] = 0x01; // configurationVersion
        avcC[i++] = sps[1]; // AVCProfileIndication (from SPS byte after NAL header)
        avcC[i++] = sps[2]; // profile_compatibility / constraint_set
        avcC[i++] = sps[3]; // AVCLevelIndication
        avcC[i++] = 0xFC | 0x03; // reserved 6 bits | lengthSizeMinusOne=3
        avcC[i++] = 0xE0 | 0x01; // reserved 3 bits | numOfSequenceParameterSets=1
        avcC[i++] = (sps.byteLength >> 8) & 0xFF;
        avcC[i++] = sps.byteLength & 0xFF;
        avcC.set(sps, i);
        i += sps.byteLength;
        avcC[i++] = 0x01; // numOfPictureParameterSets
        avcC[i++] = (pps.byteLength >> 8) & 0xFF;
        avcC[i++] = pps.byteLength & 0xFF;
        avcC.set(pps, i);
        i += pps.byteLength;
        return avcC;
    }
    // Dump everything we tried to configure the decoder with, including the
    // first 32 bytes of the avcC parameter-set blob as hex. The avcC is the
    // most likely thing a browser rejects when codec string + dimensions look
    // ordinary — its byte layout encodes profile_idc, level_idc, NALU length
    // size, and the embedded SPS/PPS, any of which can trip a stricter
    // implementation (observed on Dia 2026-05). Called from both the probe
    // (when isConfigSupported rejects every hint) and the decoder error
    // callback (when isConfigSupported says yes but configure() rejects async).
    logRejectedConfig(reason, err) {
        if (!this.videoTrack || !this.videoTrack.codec || !this.videoTrack.description)
            return;
        const dump = {
            error: err,
            codec: this.videoTrack.codec,
            width: this.videoTrack.width,
            height: this.videoTrack.height,
            hwAccel: this.hwAccel,
            descriptionBytes: this.videoTrack.description.byteLength,
            descriptionHexFirst32: Array.from(this.videoTrack.description.slice(0, 32))
                .map(b => b.toString(16).padStart(2, "0")).join(""),
        };
        // eslint-disable-next-line no-console
        console.warn(`[playback] ${reason}`, dump);
    }
    makeDecoder() {
        const d = new VideoDecoder({
            output: frame => this.handleFrame(frame),
            error: err => {
                // Decoder enters terminal 'closed' state on hard error. Two main causes:
                // (a) configure() rejected async on a codec/avcC the browser declined,
                // (b) a decoded frame triggered a runtime error after first paint.
                // Dump the config we tried because isConfigSupported() can lie (returns
                // supported:true then configure() still fails — observed on cs2347's
                // 3072x1728 avc1.640032 prefer-software path 2026-05). Try the next
                // hardwareAcceleration hint before surfacing to the React shell.
                this.logRejectedConfig("decoder error", err);
                if (this.tryNextHwAccel("decoder error"))
                    return;
                if (!this.destroyed)
                    this.runtimeOpts.onError?.();
            },
        });
        this.configureDecoder(d);
        return d;
    }
    configureDecoder(d) {
        if (!this.videoTrack || !this.videoTrack.codec || !this.videoTrack.description)
            return;
        d.configure({
            codec: this.videoTrack.codec,
            description: this.videoTrack.description,
            codedWidth: this.videoTrack.width,
            codedHeight: this.videoTrack.height,
            hardwareAcceleration: this.hwAccel,
        });
    }
    resetAndReconfigure() {
        if (this.decoder && this.decoder.state !== "closed") {
            try {
                this.decoder.close();
            }
            catch { /* noop */ }
        }
        this.decoder = this.makeDecoder();
        this.needsKeyframe = true;
        this.firstFrameSeen = false;
        this.feedStartedAt = 0;
    }
    // ---- Body buffer management ---------------------------------------------
    appendBody(bytes, absOffset) {
        if (!this.bodyBytes)
            return;
        const rel = absOffset - this.bodyOffset;
        if (rel < 0)
            return;
        const need = rel + bytes.byteLength;
        // B6: hard memory ceiling. With B1 active, a non-Range-supporting
        // server can't smuggle a corrupt 200 response into our buffer, but
        // a missing Content-Length combined with an oversized clip could still
        // OOM the tab. Refuse growth past BODY_BYTES_CEILING and surface a
        // clear error rather than crash the page. Also refuse growth past
        // totalSize when known — caps any blast radius if a server adds bytes
        // past Content-Length (e.g. ill-behaved chunked transfer).
        if (need > Playback.BODY_BYTES_CEILING) {
            this.loadState = "error";
            throw new Error(`body buffer ceiling exceeded: ${need} > ${Playback.BODY_BYTES_CEILING} — clip too large for in-memory streaming`);
        }
        if (this.totalSize && this.bodyOffset + need > this.totalSize) {
            this.loadState = "error";
            throw new Error(`body buffer would exceed totalSize ${this.totalSize}: appending ${bytes.byteLength} bytes at offset ${absOffset}`);
        }
        if (need > this.bodyBytes.byteLength) {
            let cap = this.bodyBytes.byteLength * 2 || (1024 * 1024);
            while (cap < need)
                cap *= 2;
            // Cap allocation at the ceiling — we just verified need <= ceiling.
            if (cap > Playback.BODY_BYTES_CEILING)
                cap = Playback.BODY_BYTES_CEILING;
            const grown = new Uint8Array(cap);
            grown.set(this.bodyBytes.subarray(0, this.bodyLen));
            this.bodyBytes = grown;
        }
        this.bodyBytes.set(bytes, rel);
        if (need > this.bodyLen)
            this.bodyLen = need;
    }
    parseBody() {
        if (!this.bodyBytes)
            return;
        if (!this.isFragmented)
            return; // non-frag samples come from stbl
        while (true) {
            const rel = this.parseCursor - this.bodyOffset;
            if (rel + 8 > this.bodyLen)
                break;
            const size = this.readU32(this.bodyBytes, rel);
            const type = this.readType(this.bodyBytes, rel + 4);
            let boxSize, hdrSize = 8;
            if (size === 1) {
                if (rel + 16 > this.bodyLen)
                    break;
                boxSize = this.readU64(this.bodyBytes, rel + 8);
                hdrSize = 16;
            }
            else if (size === 0) {
                break;
            }
            else {
                boxSize = size;
            }
            if (rel + boxSize > this.bodyLen)
                break;
            if (type === "moof") {
                this.parseMoof(this.bodyBytes, rel + hdrSize, rel + boxSize, this.parseCursor);
            }
            this.parseCursor += boxSize;
        }
        this.updateBuffered();
    }
    parseMoof(buf, start, end, moofFileOffset) {
        this.walkBoxes(buf, start, end, (t, s, e, h) => {
            if (t === "traf")
                this.parseTraf(buf, s + h, e, moofFileOffset);
        });
    }
    // Box-level read of one traf: tfhd defaults, tfdt, and every trun. Shared
    // by parseTraf (sample accrual) and probeFragmentedDuration (tail end time).
    readTraf(buf, start, end) {
        const tfhdHolder = { value: null };
        let tfdt = 0;
        const truns = [];
        this.walkBoxes(buf, start, end, (t, s, _e, h) => {
            if (t === "tfhd") {
                const flags = ((buf[s + h + 1] << 16) | (buf[s + h + 2] << 8) | buf[s + h + 3]) >>> 0;
                let p = s + h + 4;
                const trackID = this.readU32(buf, p);
                p += 4;
                const tf = {
                    trackID, flags,
                    baseDataOffset: null, sampleDescIndex: null,
                    defaultSampleDuration: 0, defaultSampleSize: 0, defaultSampleFlags: 0,
                    defaultBaseIsMoof: !!(flags & 0x20000),
                };
                if (flags & 0x1) {
                    tf.baseDataOffset = this.readU64(buf, p);
                    p += 8;
                }
                if (flags & 0x2) {
                    tf.sampleDescIndex = this.readU32(buf, p);
                    p += 4;
                }
                if (flags & 0x8) {
                    tf.defaultSampleDuration = this.readU32(buf, p);
                    p += 4;
                }
                if (flags & 0x10) {
                    tf.defaultSampleSize = this.readU32(buf, p);
                    p += 4;
                }
                if (flags & 0x20) {
                    tf.defaultSampleFlags = this.readU32(buf, p);
                    p += 4;
                }
                tfhdHolder.value = tf;
            }
            else if (t === "tfdt") {
                const version = buf[s + h];
                tfdt = version === 1 ? this.readU64(buf, s + h + 4) : this.readU32(buf, s + h + 4);
            }
            else if (t === "trun") {
                const version = buf[s + h];
                const flags = ((buf[s + h + 1] << 16) | (buf[s + h + 2] << 8) | buf[s + h + 3]) >>> 0;
                let p = s + h + 4;
                const count = this.readU32(buf, p);
                p += 4;
                const trun = { count, dataOffset: 0, firstSampleFlags: null, samples: [] };
                if (flags & 0x1) {
                    trun.dataOffset = (this.readU32(buf, p) | 0);
                    p += 4;
                }
                if (flags & 0x4) {
                    trun.firstSampleFlags = this.readU32(buf, p);
                    p += 4;
                }
                for (let i = 0; i < count; i++) {
                    const sample = {};
                    if (flags & 0x100) {
                        sample.duration = this.readU32(buf, p);
                        p += 4;
                    }
                    if (flags & 0x200) {
                        sample.size = this.readU32(buf, p);
                        p += 4;
                    }
                    if (flags & 0x400) {
                        sample.flags = this.readU32(buf, p);
                        p += 4;
                    }
                    if (flags & 0x800) {
                        sample.ctsOffset = version === 0 ? this.readU32(buf, p) : (this.readU32(buf, p) | 0);
                        p += 4;
                    }
                    trun.samples.push(sample);
                }
                truns.push(trun);
            }
        });
        return { tfhd: tfhdHolder.value, tfdt, truns };
    }
    parseTraf(buf, start, end, moofFileOffset) {
        const { tfhd: _tfhd, tfdt, truns } = this.readTraf(buf, start, end);
        if (!_tfhd)
            return;
        const baseDataOffset = _tfhd.baseDataOffset !== null
            ? _tfhd.baseDataOffset
            : moofFileOffset;
        const isVideo = this.videoTrack && _tfhd.trackID === this.videoTrack.id;
        if (!isVideo)
            return;
        let dts = tfdt;
        for (const trun of truns) {
            let cursor = baseDataOffset + trun.dataOffset;
            for (let i = 0; i < trun.samples.length; i++) {
                const s = trun.samples[i];
                const dur = s.duration !== undefined ? s.duration : _tfhd.defaultSampleDuration;
                const size = s.size !== undefined ? s.size : _tfhd.defaultSampleSize;
                let sflags;
                if (i === 0 && trun.firstSampleFlags !== null)
                    sflags = trun.firstSampleFlags;
                else if (s.flags !== undefined)
                    sflags = s.flags;
                else
                    sflags = _tfhd.defaultSampleFlags;
                const isKey = !(sflags & 0x00010000);
                const ctsOff = s.ctsOffset ?? 0;
                const rec = {
                    byteOffset: cursor,
                    size,
                    dts,
                    cts: dts + ctsOff,
                    duration: dur,
                    isKey,
                };
                this.videoSamples.push(rec);
                const sliced = this.sliceSample(rec);
                if (sliced && this.videoTrack && this.videoTrack.codecKind) {
                    this.cacheSeiForSample(rec, sliced, this.videoTrack.codecKind);
                }
                cursor += size;
                dts += dur;
            }
        }
    }
    cacheSeiForSample(rec, bytes, codec) {
        if (!this.videoTrack)
            return;
        const ctsMicros = Math.round(rec.cts * 1e6 / this.videoTrack.timescale);
        if (this.seiByCtsMicros.has(ctsMicros))
            return;
        const parsed = parseB2FromSample(bytes, codec);
        if (parsed) {
            this.seiByCtsMicros.set(ctsMicros, parsed);
            this.lastSeiSeen = parsed;
        }
    }
    extractAllSeiNonFrag() {
        if (!this.videoTrack || !this.videoTrack.codecKind)
            return;
        for (const rec of this.videoSamples) {
            const sliced = this.sliceSample(rec);
            if (sliced)
                this.cacheSeiForSample(rec, sliced, this.videoTrack.codecKind);
        }
    }
    // ---- Playback -----------------------------------------------------------
    play() {
        if (this.playing)
            return;
        if (!this.videoTrack)
            return;
        if (this.loadState === "error")
            return;
        // Pressing play while parked at the end replays the clip rather than
        // sitting still, and re-arms the one-shot end report so a playlist can
        // advance off the replay too.
        if (this.durationSec > 0 && this.playheadSec >= this.durationSec) {
            this.endReported = false;
            this.seek(0);
        }
        // B3: don't start playback before there are enough bytes to make it
        // through the first ~2s. With Phase A faststart + streaming, the body
        // arrives over multiple seconds; play()ing from onReady immediately
        // would burn through the decoder feeder, then stall on missing
        // samples and freeze the UI. Defer until updateBuffered() catches up.
        // body-complete bypasses the gate (covers legacy moov-at-end clips
        // and short clips where durationSec < MIN_PLAYABLE_SECONDS).
        const enoughBuffered = this.bufferedMaxSec >= Playback.MIN_PLAYABLE_SECONDS && this.durationSettled;
        const allBytesPresent = this.loadState === "body-complete";
        if (!enoughBuffered && !allBytesPresent) {
            // Defer until buffer catches up. Surface UI feedback so the user
            // doesn't repeatedly tap a dead-feeling play button. The watchdog
            // is NOT armed here (Codex P1 — armed in this path, the timeout
            // measures network buffering time, not decoder-stall time, and
            // would spuriously surface "Stream too slow" for legitimately slow
            // initial loads). The watchdog arms only after maybeStartPendingPlay
            // promotes us into the actual decoding path.
            this.pendingPlay = true;
            this.reportPlaying();
            if (this.playBtn)
                this.playBtn.textContent = "Loading…";
            return;
        }
        this.playing = true;
        this.reportPlaying();
        this.pendingPlay = false;
        this.reportPlaying();
        this.playheadAtStart = this.playheadSec;
        this.startWallTime = performance.now();
        if (this.playBtn)
            this.playBtn.textContent = "Pause";
        if (!this.decoder || this.decoder.state !== "configured") {
            this.resetAndReconfigure();
            const ts = this.playheadSec * this.videoTrack.timescale;
            this.nextVideoIdx = this.findKeyframeIdx(ts);
            this.feedVideo(this.playheadSec);
        }
        // Arm the watchdog only now that the decoder is actually being fed.
        // If 10s pass without firstFrameSeen, the stream is genuinely broken.
        this.armFirstFrameWatchdog();
        if (this.rafHandle === null)
            this.tick();
    }
    // Called from updateBuffered() and finalizeBodyComplete(). If the user
    // (or onReady's autoplay) requested play before enough was buffered,
    // try again now that the cursor advanced.
    maybeStartPendingPlay() {
        if (!this.pendingPlay)
            return;
        if (this.loadState === "error") {
            this.pendingPlay = false;
            this.reportPlaying();
            return;
        }
        const enoughBuffered = this.bufferedMaxSec >= Playback.MIN_PLAYABLE_SECONDS && this.durationSettled;
        const allBytesPresent = this.loadState === "body-complete";
        if (!enoughBuffered && !allBytesPresent)
            return;
        this.pendingPlay = false;
        this.play();
    }
    // First-frame stall watchdog. If the decoder is configured + samples are
    // being fed but no VideoFrame has rendered in FIRST_FRAME_TIMEOUT_MS,
    // assume the stream is too slow / unusable and surface a hint via overlay
    // + onError. The shell can swap to <video> fallback. Cancels itself
    // automatically on first frame (handleFrame clears the timer).
    armFirstFrameWatchdog() {
        if (this.firstFrameWatchdog !== null)
            return;
        if (this.firstFrameSeen)
            return;
        this.firstFrameWatchdog = setTimeout(() => {
            this.firstFrameWatchdog = null;
            if (this.destroyed || this.firstFrameSeen)
                return;
            // If a B8 network failure already flipped us to error + surfaced an
            // overlay, do NOT double-fire. The shell's onError handler decided
            // whether to swap to <video>; we'd just clobber its overlay text
            // and trigger a second onError("first-frame-timeout") it never
            // asked for.
            if (this.loadState === "error")
                return;
            this.setOverlay("Stream too slow — switching playback…");
            try {
                this.runtimeOpts.onError?.("first-frame-timeout");
            }
            catch { /* noop */ }
        }, Playback.FIRST_FRAME_TIMEOUT_MS);
    }
    pause() {
        // Always clear the first-frame watchdog on pause. The watchdog was
        // armed for the just-started playback; if the user pauses before any
        // frame paints (slow startup on cs2347-class 4K clips), the 10s timer
        // would still fire and surface "Stream too slow — switching playback"
        // even though the user explicitly paused. This must fire on both
        // branches (pendingPlay AND playing=true).
        if (this.firstFrameWatchdog !== null) {
            clearTimeout(this.firstFrameWatchdog);
            this.firstFrameWatchdog = null;
        }
        // Pausing while a deferred autoplay is pending must also cancel that
        // pending intent — otherwise the next chunk arrival's
        // maybeStartPendingPlay would fire play() and start the clip against
        // the user's most recent click.
        if (this.pendingPlay) {
            this.pendingPlay = false;
            this.reportPlaying();
            if (this.playBtn)
                this.playBtn.textContent = "Play";
            return;
        }
        if (!this.playing)
            return;
        this.playheadSec = this.playheadAtStart + (performance.now() - this.startWallTime) / 1000;
        this.playing = false;
        this.reportPlaying();
        if (this.playBtn)
            this.playBtn.textContent = "Play";
    }
    // Toggle treats a pendingPlay state as if the user is playing — clicking
    // the "Loading…" button cancels the pending intent. Without this, the
    // user has no way to cancel a deferred autoplay from the UI.
    toggle() {
        if (this.playing || this.pendingPlay)
            this.pause();
        else
            this.play();
    }
    seek(sec) {
        if (!this.videoTrack || this.videoSamples.length === 0)
            return;
        if (this.loadState === "error")
            return;
        // An unsettled fragmented length is 0; the buffered clamp below still
        // bounds the seek, so only clamp to a length that is known.
        sec = Math.max(0, this.durationSec > 0 ? Math.min(sec, this.durationSec) : sec);
        // Cap seek into buffered region. For streaming-body, bufferedMaxSec is
        // the strictly-bytes-present cursor (B2); for body-complete it's
        // durationSec. Either way, seeking past it would attempt to decode
        // samples whose bytes haven't arrived and silently drop frames.
        // The clamp must fire even when bufferedMaxSec === 0 (no samples yet
        // present in the streaming-body window). With a `> 0` guard, dragging
        // the seek bar before any sample bytes arrive would jump anywhere in
        // the full sample table, after which feedVideo() hits sliceSample()
        // === null and renders nothing.
        if (this.loadState !== "body-complete") {
            sec = Math.min(sec, this.bufferedMaxSec);
        }
        const targetTs = sec * this.videoTrack.timescale;
        let kfIdx = this.findKeyframeIdx(targetTs);
        // For B-frame-heavy clips (cs2347-class content), CTS and DTS can be
        // reordered: a keyframe at decode position k may have a higher CTS
        // than the non-keyframe at decode position k+1. findKeyframeIdx scans
        // the whole sample table by CTS and could return an index past
        // bufferedSampleIdx whose byte range hasn't arrived yet — sliceSample
        // would then return null and the seek silently lands on no frame.
        // Cap at the last buffered keyframe in streaming-body. If no buffered
        // keyframe exists yet (bufferedSampleIdx is 0 or no key sample within
        // it), refuse the seek entirely rather than land on a non-key sample
        // whose bytes are absent — silent canvas freeze is the worst outcome.
        // Non-frag only: bufferedSampleIdx is the stbl byte cursor there. A
        // fragmented clip's videoSamples holds only parsed fragments and never
        // advances that cursor, so the cap refused every mid-download seek
        // (Investigator citations landed at 0); the bufferedMaxSec clamp above
        // already bounds it.
        if (this.loadState === "streaming-body" && !this.isFragmented && kfIdx >= this.bufferedSampleIdx) {
            let lastBufferedKf = -1;
            const cap = Math.min(this.bufferedSampleIdx, this.videoSamples.length);
            for (let i = cap - 1; i >= 0; i--) {
                if (this.videoSamples[i].isKey) {
                    lastBufferedKf = i;
                    break;
                }
            }
            if (lastBufferedKf < 0)
                return; // no playable seek target yet
            kfIdx = lastBufferedKf;
        }
        const kfSec = this.videoSamples[kfIdx].cts / this.videoTrack.timescale;
        this.resetAndReconfigure();
        while (this.frameQueue.length > 0) {
            try {
                this.frameQueue.shift().close();
            }
            catch { /* noop */ }
        }
        // Clear stale "last seen" SEI on backward seek — otherwise the
        // previous-camera-name flashes on frames before any new SEI lookup.
        // Forward seek into an unseen region would also benefit from this
        // (no SEI for the next K frames → drawHud falls back to bCamera).
        this.lastSeiSeen = null;
        this.endReported = false;
        this.nextVideoIdx = kfIdx;
        this.playheadSec = kfSec;
        this.playheadAtStart = kfSec;
        this.startWallTime = performance.now();
        this.minAcceptTsMicros = Math.round(kfSec * 1e6);
        if (!this.playing)
            this.seekPending = true;
        this.feedVideo(kfSec);
        if (!this.playing && this.decoder && this.decoder.state === "configured") {
            this.decoder.flush().catch(() => { });
            this.needsKeyframe = true;
        }
        if (this.rafHandle === null)
            this.rafHandle = requestAnimationFrame(() => this.tick());
        this.updateUi();
    }
    findKeyframeIdx(targetTs) {
        let idx = 0;
        for (let i = 0; i < this.videoSamples.length; i++) {
            if (this.videoSamples[i].cts > targetTs)
                break;
            if (this.videoSamples[i].isKey)
                idx = i;
        }
        return idx;
    }
    tick() {
        this.rafHandle = null;
        if (this.destroyed)
            return;
        if (this.playing) {
            const now = performance.now();
            const desired = this.playheadAtStart + (now - this.startWallTime) / 1000;
            // Throttle against the streaming buffer (Codex P1). Without this,
            // tick advances the playhead past bufferedMaxSec on any clip whose
            // download rate drops below realtime after the initial gate. The
            // canvas freezes while the time UI keeps running ahead. When the
            // playhead would land past where bytes have arrived, hold it at
            // bufferedMaxSec and rebase the wall clock so playback resumes
            // smoothly once more bytes arrive (updateBuffered will widen the
            // cap on the next chunk).
            //
            // Cap polarity matters: only body-complete (all bytes present)
            // unlocks durationSec. moov-parsed (head probe absorbed but body
            // stream not yet opened) and streaming-body (body chunks arriving)
            // both use bufferedMaxSec. Play() can fire in moov-parsed when
            // the probe head already covered MIN_PLAYABLE_SECONDS, so without
            // including it in the throttle window the playhead races for the
            // ~100-500ms before streamRest flips state to streaming-body.
            const cap = this.loadState === "body-complete"
                ? this.durationSec
                : this.bufferedMaxSec;
            if (desired > cap) {
                this.playheadSec = cap;
                this.playheadAtStart = cap;
                this.startWallTime = now;
            }
            else {
                this.playheadSec = desired;
            }
            if (this.durationSec > 0 && this.playheadSec >= this.durationSec) {
                this.playheadSec = this.durationSec;
                this.pause();
                // Report the end exactly once per playthrough. seek() re-arms it, so
                // replaying the clip reports its own end again.
                if (!this.endReported) {
                    this.endReported = true;
                    try {
                        this.runtimeOpts.onEnded?.();
                    }
                    catch { /* noop */ }
                }
            }
            this.feedVideo(this.playheadSec);
        }
        this.renderQueued();
        this.updateUi();
        if (this.playing || this.seekPending) {
            this.rafHandle = requestAnimationFrame(() => this.tick());
        }
    }
    feedVideo(uptoSec) {
        if (!this.videoTrack || !this.decoder)
            return;
        if (this.needsKeyframe) {
            const ts = uptoSec * this.videoTrack.timescale;
            this.nextVideoIdx = this.findKeyframeIdx(ts);
        }
        const lookaheadTs = (uptoSec + 0.5) * this.videoTrack.timescale;
        const QUEUE_HIGH = 12;
        while (this.nextVideoIdx < this.videoSamples.length) {
            const s = this.videoSamples[this.nextVideoIdx];
            if (s.dts > lookaheadTs)
                break;
            if ((this.decoder.decodeQueueSize ?? 0) >= QUEUE_HIGH)
                break;
            const bytes = this.sliceSample(s);
            if (!bytes)
                break;
            // Cache SEI lazily — for fragmented MP4s the parseTraf path may have
            // run before the matching mdat bytes were buffered, so sliceSample
            // returned null at parse time. Now that we have bytes, retry.
            const codecKind = this.videoTrack.codecKind;
            if (codecKind) {
                this.cacheSeiForSample(s, bytes, codecKind);
            }
            // mpengine writes in-band SPS/PPS + SEI before each IDR. WebCodecs
            // VideoDecoder configured with `description` (avcC/hvcC) rejects chunks
            // that include duplicate parameter sets — surfaces as
            // `EncodingError: Decoding error.` and zero output frames. Strip non-VCL
            // NALs before feeding; SEI is already parsed for the HUD on the raw
            // bytes above via cacheSeiForSample().
            const vclOnly = codecKind ? this.stripNonVclNals(bytes, codecKind) : bytes;
            try {
                this.decoder.decode(new EncodedVideoChunk({
                    type: s.isKey ? "key" : "delta",
                    timestamp: Math.round(s.cts * 1e6 / this.videoTrack.timescale),
                    duration: Math.round(s.duration * 1e6 / this.videoTrack.timescale),
                    data: vclOnly,
                }));
                if (s.isKey)
                    this.needsKeyframe = false;
            }
            catch (e) {
                // eslint-disable-next-line no-console
                console.warn("[playback] decode error:", e);
                break;
            }
            this.nextVideoIdx++;
        }
        // Chrome's software H.264 decoder buffers input for 4K content and won't
        // emit output until enough chunks arrive (or a flush is called). On
        // startup of a 4K clip with only ~12 chunks queued, we can sit forever
        // with a black canvas. If we've fed at least the lookahead window but
        // no frame has appeared, kick the decoder with a flush.
        this.maybeFlushOnStartupStall();
    }
    // Track whether any frame has been emitted since the last reset. If not
    // and we've been feeding for a while, force a flush to drain Chrome's
    // input buffer (4K H.264 / HEVC stall workaround).
    firstFrameSeen = false;
    feedStartedAt = 0;
    // Wall-clock of the last frame paint. Used by the stall watchdog to
    // detect "decoder configured + chunks fed but no frames emitted" after
    // playback has started — symptom of a hardware decoder silently
    // refusing the bitstream (e.g. cam4's variable-frame-rate clips on
    // certain Chrome hardware-decoder paths). Initialized at first paint
    // by handleFrame().
    lastFramePaintAt = 0;
    stallDetected = false;
    maybeFlushOnStartupStall() {
        if (!this.decoder || this.decoder.state !== "configured")
            return;
        if (this.firstFrameSeen) {
            this.maybeReportRunningStall();
            return;
        }
        if (this.feedStartedAt === 0) {
            this.feedStartedAt = performance.now();
            return;
        }
        // 500ms with no output → flush. The flushed decoder requires a key
        // chunk next, which the feedVideo loop will rewind to find.
        if (performance.now() - this.feedStartedAt > 500) {
            this.feedStartedAt = 0; // re-arm so we don't flush repeatedly
            this.decoder.flush().catch(() => { });
            this.needsKeyframe = true;
        }
    }
    // Once decoding is running, watch for the case where chunks keep being
    // fed but frames stop coming back — happens when a hardware decoder
    // silently refuses a clip after the first frame. After 3 s of "fed
    // chunks, no new frames, playhead supposedly advancing," report to the
    // shell via runtimeOpts.onError so DragonPlayer can swap to <video>.
    maybeReportRunningStall() {
        if (this.stallDetected || this.destroyed)
            return;
        if (!this.playing)
            return;
        if (this.lastFramePaintAt === 0)
            return;
        const sincePaint = performance.now() - this.lastFramePaintAt;
        // Pure-streaming guard: the decoder may legitimately wait for more
        // chunks. Only flag if we're feeding (queue non-zero recently) but
        // not getting output.
        if (sincePaint > 3000) {
            // Hardware decoder may have silently refused the bitstream — try the
            // next hwAccel hint before giving up. logRejectedConfig dumps the
            // config we tried for the diagnostic.
            this.logRejectedConfig("stall >3s — no new frame");
            if (this.tryNextHwAccel("stall >3s")) {
                this.lastFramePaintAt = performance.now();
                return;
            }
            this.stallDetected = true;
            try {
                this.runtimeOpts.onError?.();
            }
            catch { /* noop */ }
        }
    }
    // Drop SPS/PPS/SEI/AUD/filler from a length-prefixed NAL stream, keeping
    // only VCL NALs. Required when feeding into a VideoDecoder configured with
    // an avcC/hvcC `description`: duplicate in-band parameter sets cause the
    // decoder to fail the first chunk with EncodingError. SEI is parsed for the
    // HUD separately on the raw sample bytes (parseB2FromSample).
    //
    // H.264 NAL: type = byte0 & 0x1F. VCL = 1..5.
    // HEVC NAL: type = (byte0 >> 1) & 0x3F. VCL = 0..31.
    stripNonVclNals(bytes, codec) {
        const out = new Uint8Array(bytes.byteLength);
        let outLen = 0;
        let p = 0;
        while (p + 4 <= bytes.byteLength) {
            const len = this.readU32(bytes, p);
            if (len === 0 || p + 4 + len > bytes.byteLength)
                break;
            const nalHeader = bytes[p + 4];
            let isVcl;
            if (codec === "h264") {
                const t = nalHeader & 0x1F;
                isVcl = t >= 1 && t <= 5;
            }
            else {
                const t = (nalHeader >> 1) & 0x3F;
                isVcl = t <= 31;
            }
            if (isVcl) {
                out.set(bytes.subarray(p, p + 4 + len), outLen);
                outLen += 4 + len;
            }
            p += 4 + len;
        }
        return out.subarray(0, outLen);
    }
    // Get bytes for a sample. Works for both fragmented (bodyBytes covers
    // bodyOffset+) and non-fragmented (bodyBytes covers entire file at offset 0).
    sliceSample(s) {
        if (!this.bodyBytes)
            return null;
        const rel = s.byteOffset - this.bodyOffset;
        if (rel < 0 || rel + s.size > this.bodyLen)
            return null;
        return this.bodyBytes.subarray(rel, rel + s.size);
    }
    updateBuffered() {
        this.computeBuffered();
        // Repaint the download fill even while paused (tick only runs playing).
        this.updateUi();
    }
    computeBuffered() {
        if (this.videoSamples.length === 0 || !this.videoTrack)
            return;
        // For fragmented streams, videoSamples is itself the buffered set:
        // new samples appear when their moof is parsed (and the matching mdat
        // bytes have already been appended in the same chunk pass). The "last
        // sample" shortcut is correct.
        //
        // For loadComplete / body-complete (any path), all samples are present.
        //
        // For moov-at-head non-frag in moov-parsed OR streaming-body,
        // videoSamples is the FULL sample list (stbl, populated at moov-parse)
        // but only the samples whose byteOffset+size <= bodyOffset+bodyLen
        // are actually playable. Advance a monotonic cursor (B2) so seek/play
        // can gate on it without seeing a phantom durationSec. The signal
        // "moov-at-head non-frag" is bodyOffset > 0 with isFragmented=false;
        // moov-at-end legacy has bodyOffset === 0 and falls through to the
        // last-sample shortcut (which is correct since fetchEntireFile loaded
        // everything before reaching that state).
        const isStreamingNonFrag = !this.isFragmented
            && this.bodyOffset > 0
            && this.loadState !== "body-complete";
        if (isStreamingNonFrag) {
            const bodyEnd = this.bodyOffset + this.bodyLen;
            while (this.bufferedSampleIdx < this.videoSamples.length) {
                const s = this.videoSamples[this.bufferedSampleIdx];
                if (s.byteOffset + s.size > bodyEnd)
                    break;
                this.bufferedSampleIdx++;
            }
            // Include the LAST buffered sample's duration so bufferedMaxSec is
            // the seek-target ceiling (matches the body-complete shortcut at
            // L1874 which uses last.cts + last.duration). Without `+ duration`,
            // the MIN_PLAYABLE_SECONDS gate trips one sample later than
            // intended and seek() rejects the final buffered second.
            if (this.bufferedSampleIdx > 0) {
                const last = this.videoSamples[this.bufferedSampleIdx - 1];
                this.bufferedMaxSec = (last.cts + last.duration) / this.videoTrack.timescale;
            }
            else {
                this.bufferedMaxSec = 0;
            }
            // durationSec for streaming-body comes from the moov's mvhd/mdhd, not
            // from the buffered samples — set once at parse, never narrowed here.
            return;
        }
        const last = this.videoSamples[this.videoSamples.length - 1];
        this.bufferedMaxSec = (last.cts + last.duration) / this.videoTrack.timescale;
        // Unsettled fragmented length stays 0 so nothing draws against the
        // downloaded length (the bar raced to 100% then snapped back).
        if (!this.durationSettled)
            return;
        if (this.durationEstimated && this.totalSize > 0 && this.loadState !== "body-complete") {
            // Parsed bytes, not received bytes: bufferedMaxSec covers parsed
            // fragments only, and counting a half-received one dips the estimate.
            const have = this.parseCursor;
            const est = have > this.moovEnd ? this.bufferedMaxSec * (this.totalSize - this.moovEnd) / (have - this.moovEnd) : 0;
            // Recomputed each chunk rather than ratcheted: a small early fragment
            // must not pin an overshoot until body-complete snaps it back.
            this.durationSec = est;
        }
        if (this.durationSec < this.bufferedMaxSec)
            this.durationSec = this.bufferedMaxSec;
    }
    updateUi() {
        if (this.seekEL && this.durationSec <= 0) {
            this.seekEL.style.removeProperty("--played");
            this.seekEL.style.removeProperty("--buffered");
        }
        if (this.seekEL && this.durationSec > 0) {
            const v = Math.min(1000, Math.round(1000 * this.playheadSec / this.durationSec));
            if (document.activeElement !== this.seekEL)
                this.seekEL.value = String(v);
            // Played and downloaded extents for the track fill (styles.css).
            const buffered = this.loadState === "body-complete" ? 1 : Math.min(1, this.bufferedMaxSec / this.durationSec);
            this.seekEL.style.setProperty("--played", `${(100 * Math.min(1, this.playheadSec / this.durationSec)).toFixed(2)}%`);
            this.seekEL.style.setProperty("--buffered", `${(100 * buffered).toFixed(2)}%`);
        }
        if (this.timeEL) {
            this.timeEL.textContent = this.fmt(this.playheadSec) + " / " + this.fmt(this.durationSec);
        }
        try {
            this.runtimeOpts.onTimeUpdate?.(this.playheadSec);
        }
        catch { /* noop */ }
    }
    fmt(sec) {
        if (!isFinite(sec) || sec < 0)
            sec = 0;
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60);
        return m + ":" + (s < 10 ? "0" : "") + s;
    }
    // ---- Frame render -------------------------------------------------------
    handleFrame(frame) {
        if (this.destroyed) {
            frame.close();
            return;
        }
        if (frame.timestamp < this.minAcceptTsMicros) {
            frame.close();
            return;
        }
        const wasFirst = !this.firstFrameSeen;
        this.firstFrameSeen = true;
        // First frame rendered — kill any pending stall watchdog (B3).
        if (this.firstFrameWatchdog !== null) {
            clearTimeout(this.firstFrameWatchdog);
            this.firstFrameWatchdog = null;
        }
        this.frameQueue.push(frame);
        while (this.frameQueue.length > 30) {
            try {
                this.frameQueue.shift().close();
            }
            catch { /* noop */ }
        }
        if (this.rafHandle === null)
            this.rafHandle = requestAnimationFrame(() => this.tick());
        // Notify the React shell exactly once when the first frame is queued for
        // render — the spinner overlay hides at this signal.
        if (wasFirst) {
            // Cache the working hint for this codec+resolution so subsequent
            // Playback instances skip the decoder-error-then-fallthrough wait.
            // Only fires once per instance (gated on firstFrameSeen flip).
            if (this.videoTrack && this.videoTrack.codec) {
                Playback.recordWorkingHwAccel(this.videoTrack.codec, this.videoTrack.width, this.videoTrack.height, this.hwAccel);
                // If we got here via the rebuilt-avcC path, remember the fingerprint
                // so the next clip with the same codec+resolution applies the
                // rebuild proactively (skips the 3-hint decoder-error sequence).
                if (this.rebuiltAvcCAttempted) {
                    Playback.recordRequiresRebuiltAvcC(this.videoTrack.codec, this.videoTrack.width, this.videoTrack.height);
                }
            }
            try {
                this.runtimeOpts.onFirstFrame?.();
            }
            catch { /* noop */ }
        }
    }
    renderQueued() {
        if (this.frameQueue.length === 0)
            return;
        const playheadMicros = this.playheadSec * 1e6;
        let toRender = null;
        if (this.playing) {
            while (this.frameQueue.length > 0 && this.frameQueue[0].timestamp <= playheadMicros) {
                if (toRender)
                    toRender.close();
                toRender = this.frameQueue.shift();
            }
        }
        else if (this.seekPending) {
            while (this.frameQueue.length > 0 && this.frameQueue[0].timestamp <= playheadMicros) {
                if (toRender)
                    toRender.close();
                toRender = this.frameQueue.shift();
            }
            if (!toRender && this.frameQueue.length > 0)
                toRender = this.frameQueue.shift();
            if (toRender)
                this.seekPending = false;
        }
        if (toRender) {
            this.renderFrame(toRender);
            try {
                toRender.close();
            }
            catch { /* noop */ }
        }
    }
    renderFrame(frame) {
        this.canvas.width = frame.displayWidth || frame.codedWidth;
        this.canvas.height = frame.displayHeight || frame.codedHeight;
        this.ctx.drawImage(frame, 0, 0, this.canvas.width, this.canvas.height);
        // SEI lookup, with most-recent-prior fallback for sparse SEI.
        const sei = this.seiByCtsMicros.get(frame.timestamp) ?? this.lastSeiSeen;
        if (sei)
            this.lastSeiSeen = sei;
        // Order: HUD bottom-left first; then text overlays (wallclock/POS) at
        // their explicit coords; then detection rectangles on top so a wallclock
        // overlay at (0, 0) never obscures an OD/LPR box. Without this swap,
        // adversarial review (P1-2) flagged that any POS sample at x_loc=0,
        // y_loc=0 would erase every detection rectangle below it.
        this.drawHud(sei);
        this.drawTextOverlays(sei);
        // VMD windows hidden per operator preference (D-2026-05-11): the colored
        // motion-zone rectangles read as "decoder artifacts" to non-operators,
        // and the existing burned-in mpengine overlays plus camera-name HUD
        // already convey enough. Leave the parse + drawVmdOverlay function in
        // place so we can flip this back on behind a toggle later.
        // this.drawVmdOverlay(sei);
        this.drawLprOverlay(sei);
        this.drawOdOverlay(sei);
        // Mark progress for the stall watchdog. Without this the watchdog
        // would fire any time a clip pauses naturally (end-of-clip, user
        // pause); we only care about "feeding chunks, not getting frames."
        this.lastFramePaintAt = performance.now();
    }
    // Decision [#13]: canvas-drawn HUD. cameraName + datetime burned into the
    // bottom-left, CCTV style. Survives the export-with-overlay path because
    // the encoded canvas pixels include it.
    drawHud(sei) {
        const cameraName = sei?.header.name?.trim()
            || this.runtimeOpts.fallbackCameraName
            || "Camera ?";
        const ts = sei?.header.timestamp ?? null;
        const gmt = sei?.header.gmtoffset ?? 0;
        const tsStr = ts !== null ? this.formatTimestamp(ts, gmt) : "";
        const W = this.canvas.width;
        const H = this.canvas.height;
        const fontSize = Math.max(14, Math.round(W / 80));
        const pad = Math.max(4, Math.round(fontSize / 3));
        const lineHeight = fontSize + pad;
        const lines = tsStr ? [cameraName, tsStr] : [cameraName];
        this.ctx.save();
        this.ctx.font = `bold ${fontSize}px system-ui, sans-serif`;
        this.ctx.textBaseline = "top";
        let maxW = 0;
        for (const line of lines)
            maxW = Math.max(maxW, this.ctx.measureText(line).width);
        const boxW = maxW + pad * 2;
        const boxH = lineHeight * lines.length + pad;
        const x = pad;
        const y = H - boxH - pad;
        this.ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
        this.ctx.fillRect(x, y, boxW, boxH);
        this.ctx.fillStyle = "#fff";
        for (let i = 0; i < lines.length; i++) {
            this.ctx.fillText(lines[i], x + pad, y + pad + i * lineHeight);
        }
        this.ctx.restore();
    }
    // Format SEI Unix-seconds timestamp with the NVR's local gmtoffset.
    // Renders in the NVR's local time (what the operator expects to see).
    formatTimestamp(unixSec, gmtoffsetHours) {
        // Construct a Date adjusted to the NVR's offset. JavaScript Date is
        // always UTC under the hood; we shift the timestamp by the difference
        // between viewer's local offset and the NVR's offset to display NVR-local.
        const date = new Date(unixSec * 1000);
        const viewerOffsetMin = date.getTimezoneOffset(); // minutes WEST of UTC
        const nvrOffsetMin = -gmtoffsetHours * 60; // sign flip: getTimezoneOffset is inverted
        const shifted = new Date(date.getTime() + (viewerOffsetMin - nvrOffsetMin) * 60 * 1000);
        return shifted.toLocaleString();
    }
    // Phase 4: LPR plate overlay.
    drawLprOverlay(sei) {
        if (!this.videoTrack)
            return;
        const lpr = sei?.extensions.lpr;
        if (!lpr)
            return;
        // LPR p1..p4 + ROI corners are in 0..999 normalized space (mpengine
        // convention; matches dview B2_EXT_LPR.getP1x(int width) which computes
        // `(bP1x * (width + 0.5)) / 999`). Treating them as source-pixel values
        // crowds the polygon into the top-left when canvas matches the track
        // dimensions.
        const sx = (this.canvas.width + 0.5) / 999;
        const sy = (this.canvas.height + 0.5) / 999;
        this.ctx.save();
        // Optional ROI outline (decision-Phase-4, off by default via showLprRoi).
        // roi_* is the fixed analysis window in the same 0..999 space as the
        // plate corners. Draw it first, faint + dashed, so the plate quad and
        // labels below render on top. Skip a degenerate/zero ROI (no window set).
        if (this.runtimeOpts.showLprRoi &&
            lpr.roi_ex > lpr.roi_sx &&
            lpr.roi_ey > lpr.roi_sy) {
            this.ctx.save();
            this.ctx.lineWidth = Math.max(1, Math.round(this.canvas.width / 800));
            this.ctx.setLineDash([this.ctx.lineWidth * 4, this.ctx.lineWidth * 4]);
            this.ctx.strokeStyle = "rgba(180, 180, 180, 0.5)";
            this.ctx.strokeRect(lpr.roi_sx * sx, lpr.roi_sy * sy, (lpr.roi_ex - lpr.roi_sx) * sx, (lpr.roi_ey - lpr.roi_sy) * sy);
            this.ctx.restore();
        }
        this.ctx.lineWidth = Math.max(2, Math.round(this.canvas.width / 500));
        this.ctx.strokeStyle = this.lprStateColor(lpr.state);
        this.ctx.beginPath();
        this.ctx.moveTo(lpr.p1_x * sx, lpr.p1_y * sy);
        this.ctx.lineTo(lpr.p2_x * sx, lpr.p2_y * sy);
        this.ctx.lineTo(lpr.p3_x * sx, lpr.p3_y * sy);
        this.ctx.lineTo(lpr.p4_x * sx, lpr.p4_y * sy);
        this.ctx.closePath();
        this.ctx.stroke();
        const fontSize = Math.max(14, Math.round(this.canvas.width / 80));
        this.ctx.font = `bold ${fontSize}px system-ui, sans-serif`;
        this.ctx.textBaseline = "bottom";
        this.ctx.fillStyle = "#fff";
        this.ctx.strokeStyle = "rgba(0, 0, 0, 0.8)";
        this.ctx.lineWidth = Math.max(2, Math.round(fontSize / 6));
        const labelY = lpr.p1_y * sy - 4;
        if (lpr.lpn) {
            this.ctx.strokeText(lpr.lpn, lpr.p1_x * sx, labelY);
            this.ctx.fillText(lpr.lpn, lpr.p1_x * sx, labelY);
        }
        if (lpr.last_hit_lpn && lpr.last_hit_lpn !== lpr.lpn) {
            const prevY = labelY - fontSize - 4;
            const prev = `prev: ${lpr.last_hit_lpn}`;
            this.ctx.strokeText(prev, lpr.p1_x * sx, prevY);
            this.ctx.fillText(prev, lpr.p1_x * sx, prevY);
        }
        this.ctx.restore();
    }
    // LPR state machine colors. State enum values come from b2.h's LPR state
    // field — exact bytes vary by mpengine build; we use a permissive mapping
    // (the C enum names are detecting/confirmed/lost-ish). Test fixtures use 0/1/2.
    lprStateColor(state) {
        if (state === 1)
            return "rgba(255, 220, 0, 0.9)"; // detecting (yellow)
        if (state === 2)
            return "rgba(60, 230, 100, 0.95)"; // confirmed (green)
        if (state === 3)
            return "rgba(230, 60, 60, 0.9)"; // lost (red)
        return "rgba(180, 180, 180, 0.8)"; // unknown
    }
    // Phase 5: VMD motion-detection window overlays.
    //
    // Renders a rectangle per VMD window in source-image coordinates. The
    // trigger[5] field has one byte per motion zone — non-zero means that
    // zone fired this frame. We color the window outline based on whether
    // ANY zone fired (red) vs idle (blue). VMD2 uses the same struct format
    // and renders identically but slightly dimmer to distinguish from VMD.
    // @ts-expect-error TS6133 — kept for the toggle-back path; currently
    // unreferenced because renderFrame's call site is commented out.
    drawVmdOverlay(sei) {
        if (!this.videoTrack || !sei)
            return;
        // VMD window sx/sy/ex/ey are 0..999 normalized (matches dview
        // B2_EXT_VMD.getSx(int width) which computes `(bSx * (width + 0.5)) / 999`).
        const sx = (this.canvas.width + 0.5) / 999;
        const sy = (this.canvas.height + 0.5) / 999;
        const stroke = Math.max(1, Math.round(this.canvas.width / 800));
        if (sei.extensions.vmd) {
            this.drawVmdWindows(sei.extensions.vmd, sx, sy, stroke, 1.0, /*dashed*/ false);
        }
        if (sei.extensions.vmd2) {
            // Dashed stroke distinguishes the alternate VMD channel from VMD.
            // Alpha alone at 0.6 was too subtle on busy 12-zone scenes.
            this.drawVmdWindows(sei.extensions.vmd2, sx, sy, stroke, 0.6, /*dashed*/ true);
        }
    }
    drawVmdWindows(windows, sx, sy, stroke, alphaMult, dashed) {
        this.ctx.save();
        this.ctx.lineWidth = stroke;
        if (dashed)
            this.ctx.setLineDash([stroke * 4, stroke * 4]);
        for (const w of windows) {
            const triggered = w.trigger.some(t => t > 0);
            // Active = bright red, idle = blue tint (matches dview convention).
            const color = triggered
                ? `rgba(230, 60, 60, ${0.9 * alphaMult})`
                : `rgba(80, 160, 255, ${0.4 * alphaMult})`;
            this.ctx.strokeStyle = color;
            const x = w.sx * sx;
            const y = w.sy * sy;
            const ww = Math.max(0, (w.ex - w.sx) * sx);
            const hh = Math.max(0, (w.ey - w.sy) * sy);
            this.ctx.strokeRect(x, y, ww, hh);
        }
        this.ctx.restore();
    }
    // Phase 5: aiengine object-detection boxes (B2_EXT_OD V1/V2). V2 carries
    // a 32-byte class name and a packed RGBA color the engine chose for that
    // class; V1 only has the cls integer. Rendered as a rectangle + label.
    drawOdOverlay(sei) {
        if (!this.videoTrack || !sei?.extensions.od)
            return;
        // OD x/y/width/height are 0..999 normalized for both V1 and V2 (matches
        // dview B2_EXT_OD/B2_EXT_OD_V2.getX(int width) which compute
        // `(bX * (width + 0.5)) / 999`).
        const sx = (this.canvas.width + 0.5) / 999;
        const sy = (this.canvas.height + 0.5) / 999;
        const stroke = Math.max(2, Math.round(this.canvas.width / 600));
        const fontSize = Math.max(11, Math.round(this.canvas.width / 90));
        const pad = Math.max(2, Math.round(fontSize / 4));
        this.ctx.save();
        this.ctx.lineWidth = stroke;
        this.ctx.font = `bold ${fontSize}px system-ui, sans-serif`;
        this.ctx.textBaseline = "top";
        for (const od of sei.extensions.od) {
            const x = od.x * sx;
            const y = od.y * sy;
            const ww = od.width * sx;
            const hh = od.height * sy;
            const color = od.version === 2 ? odV2Color(od.color) : "rgba(120, 230, 120, 0.95)";
            // 1px-wider black halo behind the colored stroke so engine-chosen colors
            // that match the scene (dark blue car on dark scene) don't vanish.
            // Mirrors the LPR text outline pattern at drawLprOverlay.
            this.ctx.strokeStyle = "rgba(0, 0, 0, 0.6)";
            this.ctx.lineWidth = stroke + 2;
            this.ctx.strokeRect(x, y, ww, hh);
            this.ctx.strokeStyle = color;
            this.ctx.lineWidth = stroke;
            this.ctx.strokeRect(x, y, ww, hh);
            const label = od.version === 2 && od.cls_name ? od.cls_name : `cls ${od.cls}`;
            if (label) {
                const textW = this.ctx.measureText(label).width;
                this.ctx.fillStyle = "rgba(0, 0, 0, 0.65)";
                this.ctx.fillRect(x, y, textW + pad * 2, fontSize + pad * 2);
                // Force readable label color even when engine color is very dark.
                this.ctx.fillStyle = "#fff";
                this.ctx.fillText(label, x + pad, y + pad);
            }
        }
        this.ctx.restore();
    }
    // Phase 5: OVERLAY (legacy single-POS) + POS (multi-POS) text. Both render
    // a wrapped text box at (x_loc, y_loc) in source coordinates. POS carries a
    // 32-byte terminal name; OVERLAY doesn't (legacy format). Wallclock
    // timestamps feature (`fWallclockTimestamps`) writes into OVERLAY.
    drawTextOverlays(sei) {
        if (!this.videoTrack || !sei)
            return;
        const sx = this.canvas.width / Math.max(1, this.videoTrack.width);
        const sy = this.canvas.height / Math.max(1, this.videoTrack.height);
        const fontSize = Math.max(12, Math.round(this.canvas.width / 90));
        const pad = Math.max(3, Math.round(fontSize / 3));
        this.ctx.save();
        this.ctx.font = `${fontSize}px ui-monospace, "SF Mono", Menlo, monospace`;
        this.ctx.textBaseline = "top";
        if (sei.extensions.overlay) {
            const ov = sei.extensions.overlay;
            this.drawTextBlock(ov.lines, ov.x_loc * sx, ov.y_loc * sy, fontSize, pad, null);
        }
        if (sei.extensions.pos) {
            for (const pos of sei.extensions.pos) {
                const label = pos.name && pos.name.trim() ? pos.name : null;
                this.drawTextBlock(pos.lines, pos.x_loc * sx, pos.y_loc * sy, fontSize, pad, label);
            }
        }
        this.ctx.restore();
    }
    drawTextBlock(lines, x, y, fontSize, pad, label) {
        // Filter empty lines so a 16-line buffer with one filled line doesn't draw
        // a 16-line-tall blank box.
        const filled = lines.filter(l => l && l.trim().length > 0);
        if (filled.length === 0 && !label)
            return;
        const display = label ? [label, ...filled] : filled;
        let maxW = 0;
        for (const line of display)
            maxW = Math.max(maxW, this.ctx.measureText(line).width);
        const lineHeight = fontSize + 2;
        // Clamp the box width to (canvas.width - x) so a malformed POS sample
        // with a 255-char line doesn't paint a 2000+ px backdrop across the
        // entire frame and erase detection rectangles below it (adversarial
        // review P1-1). Same for height — drop trailing lines that would
        // overflow the canvas vertically.
        const maxBoxW = Math.max(0, this.canvas.width - x);
        const maxBoxH = Math.max(0, this.canvas.height - y);
        const boxW = Math.min(maxW + pad * 2, maxBoxW);
        const linesThatFit = Math.max(1, Math.min(display.length, Math.floor((maxBoxH - pad * 2) / lineHeight)));
        const boxH = Math.min(lineHeight * linesThatFit + pad * 2, maxBoxH);
        this.ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
        this.ctx.fillRect(x, y, boxW, boxH);
        this.ctx.fillStyle = label ? "#ffd680" : "#f0f0f0"; // POS name in highlight color
        // Clip text inside the box so an over-wide line doesn't paint outside the
        // backdrop and over neighboring overlays.
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.rect(x, y, boxW, boxH);
        this.ctx.clip();
        for (let i = 0; i < linesThatFit; i++) {
            if (i === 1 && label)
                this.ctx.fillStyle = "#f0f0f0"; // body in normal color
            this.ctx.fillText(display[i], x + pad, y + pad + i * lineHeight);
        }
        this.ctx.restore();
    }
    // Public read-only HUD snapshot — used by tests and DragonPlayer to
    // mirror name/time into accessible DOM if desired.
    getCurrentHud() {
        const sei = this.lastSeiSeen;
        if (sei) {
            return {
                cameraName: sei.header.name || this.runtimeOpts.fallbackCameraName || "Camera ?",
                timestamp: sei.header.timestamp,
                gmtoffset: sei.header.gmtoffset,
                fromSei: true,
            };
        }
        return {
            cameraName: this.runtimeOpts.fallbackCameraName || "Camera ?",
            timestamp: null,
            gmtoffset: 0,
            fromSei: false,
        };
    }
    // ---- Export with overlay -----------------------------------------------
    //
    // Decode-decorate-re-encode pipeline. Runs a second VideoDecoder
    // independently of the playback decoder so playback isn't disturbed.
    // Each output VideoFrame is drawn to an OffscreenCanvas, the HUD +
    // LPR/OD/text overlays are composited from the SEI cache, and a new
    // VideoFrame is captured from the canvas and fed to a VideoEncoder.
    // Output chunks are buffered into Mp4Muxer which writes a fragmented
    // MP4 the user downloads.
    //
    // Limitations:
    //  - Output is always H.264 (avc1.*) regardless of source codec. HEVC
    //    source clips re-encode into H.264 — wider playback compat at the
    //    cost of one transcode.
    //  - Hardware encoders may reject the source codec/profile match. We
    //    set hardwareAcceleration: "prefer-hardware" and surface any
    //    encoder errors via the status display.
    async exportWithOverlay() {
        if (this.exporting)
            return;
        if (!this.videoTrack || !this.videoTrack.description || !this.videoTrack.codec) {
            this.setExportStatus("Not ready — clip still loading.");
            return;
        }
        if (this.videoSamples.length === 0) {
            this.setExportStatus("No samples to export.");
            return;
        }
        // B5: export reads every sample, so it requires every sample's bytes.
        // Streaming-body has only a prefix; running export there would skip
        // every sample whose byteOffset is past bodyOffset+bodyLen and produce
        // a truncated MP4. Refuse with a clear status; the UI button is
        // expected to also be visibly disabled (canExport()).
        if (this.loadState !== "body-complete") {
            if (this.loadState === "error") {
                this.setExportStatus("Cannot export — load error.");
            }
            else {
                this.setExportStatus("Wait for clip to finish loading.");
            }
            return;
        }
        if (typeof VideoEncoder === "undefined") {
            this.setExportStatus("Export needs WebCodecs encoder support (Chrome/Edge).");
            return;
        }
        this.exporting = true;
        this.cancelExport = false;
        if (this.exportBtn)
            this.exportBtn.textContent = "Cancel export";
        this.setExportStatus("Starting…");
        try {
            const blob = await this.runExport();
            if (this.cancelExport) {
                this.setExportStatus("Cancelled.");
                return;
            }
            if (!blob) {
                this.setExportStatus("No frames encoded.");
                return;
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = this.exportFilename();
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            this.setExportStatus(`Done — ${this.exportFilename()} (${(blob.size / 1024 / 1024).toFixed(1)} MB)`);
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error("[export] error:", e);
            this.setExportStatus("Error: " + (e.message ?? String(e)));
        }
        finally {
            this.exporting = false;
            if (this.exportBtn)
                this.exportBtn.textContent = "Download with overlay";
        }
    }
    async runExport() {
        const track = this.videoTrack;
        const W = track.width;
        const H = track.height;
        const srcTimescale = track.timescale;
        const totalFrames = this.videoSamples.length;
        const framerate = Math.round(this.estimateFramerate());
        const bitrate = this.estimateBitrate(W, H, framerate);
        const codecKind = track.codecKind;
        // OffscreenCanvas isn't on the same DOM as our playback canvas — we
        // composite into a private buffer so the live player stays interactive
        // during export. Safari shipped OffscreenCanvas in 16.4; older fallback
        // would be a hidden DOM canvas, but we already gate the export feature
        // on VideoEncoder which is even newer.
        const offscreen = new OffscreenCanvas(W, H);
        const offCtx = offscreen.getContext("2d", { willReadFrequently: false });
        if (!offCtx)
            throw new Error("offscreen 2d context unavailable");
        // Encoder always outputs H.264 main profile. avc1.4D401E = main, level
        // 3.0 (up to 720p). For larger sources we bump to level 4.2 (1080p) /
        // 5.1 (4K) using the source's level if higher.
        const outCodec = this.pickEncoderCodec(track.codec || "avc1.4D401E", W, H);
        const muxer = new Mp4Muxer({ width: W, height: H, timescale: srcTimescale });
        let encoderError = null;
        const encoder = new VideoEncoder({
            output: (chunk, meta) => {
                const desc = meta?.decoderConfig?.description;
                if (desc)
                    muxer.setDecoderConfig(desc);
                muxer.addChunk(chunk);
            },
            error: e => {
                encoderError = e;
                // eslint-disable-next-line no-console
                console.error("[export] encoder error:", e);
            },
        });
        encoder.configure({
            codec: outCodec,
            width: W,
            height: H,
            bitrate,
            framerate,
            hardwareAcceleration: "prefer-hardware",
            avc: { format: "avc" },
            bitrateMode: "variable",
        });
        let decoderError = null;
        let emittedFrames = 0;
        const keyInterval = Math.max(1, framerate * 2);
        const decoder = new VideoDecoder({
            output: async (frame) => {
                try {
                    if (this.cancelExport || encoderError || decoderError) {
                        frame.close();
                        return;
                    }
                    offCtx.drawImage(frame, 0, 0, W, H);
                    // Pull the SEI for this frame's CTS and re-run the same draw
                    // path as live playback (drawHud/text/LPR/OD). VMD intentionally
                    // omitted, matching the live player.
                    const sei = this.seiByCtsMicros.get(frame.timestamp) ?? this.lastSeiSeen;
                    if (sei)
                        this.drawOverlaysToCtx(offCtx, W, H, sei);
                    const newFrame = new VideoFrame(offscreen, {
                        timestamp: frame.timestamp,
                        duration: frame.duration ?? undefined,
                    });
                    frame.close();
                    const isKey = (emittedFrames % keyInterval) === 0;
                    encoder.encode(newFrame, { keyFrame: isKey });
                    newFrame.close();
                    emittedFrames++;
                    if (emittedFrames % 10 === 0 || emittedFrames === totalFrames) {
                        const pct = Math.round((100 * emittedFrames) / totalFrames);
                        this.setExportStatus(`Encoding ${emittedFrames}/${totalFrames} (${pct}%)…`);
                    }
                }
                catch (e) {
                    // eslint-disable-next-line no-console
                    console.error("[export] composite error:", e);
                    try {
                        frame.close();
                    }
                    catch { /* noop */ }
                }
            },
            error: e => {
                decoderError = e;
                // eslint-disable-next-line no-console
                console.error("[export] decoder error:", e);
            },
        });
        decoder.configure({
            codec: track.codec,
            description: track.description,
            codedWidth: W,
            codedHeight: H,
            hardwareAcceleration: "prefer-software",
        });
        for (let i = 0; i < this.videoSamples.length; i++) {
            if (this.cancelExport || encoderError || decoderError)
                break;
            // Backpressure: bounded queues so we don't OOM on long clips.
            while (decoder.decodeQueueSize >= 8 || encoder.encodeQueueSize >= 8) {
                if (this.cancelExport || encoderError || decoderError)
                    break;
                await new Promise(r => setTimeout(r, 10));
            }
            if (this.cancelExport || encoderError || decoderError)
                break;
            const s = this.videoSamples[i];
            const bytes = this.sliceSample(s);
            if (!bytes)
                continue;
            // Same NAL-strip discipline as the live decoder: drop in-band SPS/PPS
            // so configure(description=avcC) accepts each chunk.
            const vclOnly = codecKind ? this.stripNonVclNals(bytes, codecKind) : bytes;
            try {
                decoder.decode(new EncodedVideoChunk({
                    type: s.isKey ? "key" : "delta",
                    timestamp: Math.round(s.cts * 1e6 / srcTimescale),
                    duration: Math.round(s.duration * 1e6 / srcTimescale),
                    data: vclOnly,
                }));
            }
            catch (e) {
                decoderError = e;
                break;
            }
        }
        try {
            await decoder.flush();
        }
        catch { /* noop */ }
        try {
            await encoder.flush();
        }
        catch { /* noop */ }
        try {
            decoder.close();
        }
        catch { /* noop */ }
        try {
            encoder.close();
        }
        catch { /* noop */ }
        if (encoderError)
            throw encoderError;
        if (decoderError)
            throw decoderError;
        if (this.cancelExport)
            return null;
        this.setExportStatus("Muxing…");
        return muxer.finalize();
    }
    // Compose the SEI overlay set onto an arbitrary 2D context. Mirrors
    // renderFrame()'s draw order; uses a `with-ctx` variant of each draw fn
    // because the live methods write directly to `this.ctx`. To keep this
    // patch small we temporarily swap this.ctx + this.canvas to the export
    // surface, invoke the existing draw methods, then restore.
    drawOverlaysToCtx(ctx, w, h, sei) {
        const liveCtx = this.ctx;
        const liveCanvas = this.canvas;
        // Cast: OffscreenCanvasRenderingContext2D shares the relevant subset
        // with CanvasRenderingContext2D for our draw paths (drawImage, fillRect,
        // setLineDash, strokeText, etc). The live drawHud/drawLprOverlay/etc
        // only touch that subset.
        this.ctx = ctx;
        this.canvas = { width: w, height: h };
        try {
            this.drawHud(sei);
            this.drawTextOverlays(sei);
            // VMD intentionally omitted to match live player.
            this.drawLprOverlay(sei);
            this.drawOdOverlay(sei);
        }
        finally {
            this.ctx = liveCtx;
            this.canvas = liveCanvas;
        }
    }
    // Estimate output fps from the source's stts/sample-duration distribution.
    // 30 fps is the fleet default — only adjust if observed deltas point
    // clearly elsewhere (cam clips at 15 fps, 4K cameras at 12 fps).
    estimateFramerate() {
        if (!this.videoTrack || this.videoSamples.length < 2)
            return 30;
        const tsc = this.videoTrack.timescale;
        let totalDur = 0, n = 0;
        for (const s of this.videoSamples) {
            if (s.duration > 0) {
                totalDur += s.duration;
                n++;
            }
        }
        if (n === 0)
            return 30;
        const avgSec = (totalDur / n) / tsc;
        if (avgSec <= 0)
            return 30;
        const fps = 1 / avgSec;
        return Math.max(5, Math.min(60, Math.round(fps)));
    }
    // ~0.08 bpp baseline = decent quality for surveillance content; cap at
    // 12 Mbps so 4K clips don't balloon. Source-derived would be ideal but
    // we'd need to walk every sample twice — this heuristic is close enough.
    estimateBitrate(w, h, fps) {
        const bpp = 0.08;
        return Math.min(12_000_000, Math.round(w * h * fps * bpp));
    }
    // Map the source codec string to a safe encoder codec. avc1.* sources
    // re-encode as avc1.* keeping the source profile/level when known;
    // HEVC (hev1.*/hvc1.*) collapses to avc1.4D4028 (main 4.0) because
    // hardware HEVC encoders are scarce in browsers.
    pickEncoderCodec(sourceCodec, w, _h) {
        if (/^avc1\./i.test(sourceCodec))
            return sourceCodec;
        // Pick AVC level from width: 720p→3.1, 1080p→4.0, 4K→5.1.
        if (w > 2048)
            return "avc1.4D5033";
        if (w > 1280)
            return "avc1.4D4028";
        return "avc1.4D401F";
    }
    exportFilename() {
        const cam = this.lastSeiSeen?.header.name?.trim().replace(/[^\w-]+/g, "_") || "clip";
        const ts = this.lastSeiSeen?.header.timestamp;
        const stamp = ts
            ? new Date(ts * 1000).toISOString().slice(0, 19).replace(/[:T]/g, "-")
            : new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        return `${cam}-${stamp}.mp4`;
    }
    setExportStatus(msg) {
        if (this.exportStatus)
            this.exportStatus.textContent = msg;
    }
    // Test hooks: expose internals read-only for vitest.
    _testGetSampleCount() { return this.videoSamples.length; }
    _testGetSeiCacheSize() { return this.seiByCtsMicros.size; }
    // Snapshot of the SEI cache keyed by composition-time micros (decision
    // [#20]). Used to prove B-frame clips key metadata by cts, not sample index.
    _testGetSeiByCtsMicros() { return new Map(this.seiByCtsMicros); }
    // Drive the private draw/render paths for overlay + HUD assertions.
    _testDrawLprOverlay(sei) { this.drawLprOverlay(sei); }
    _testRenderFrame(frame) { this.renderFrame(frame); }
    _testIsDestroyed() { return this.destroyed; }
    _testGetGeneration() { return this.generation; }
    _testGetHwAccelOptions() { return [...this.hwAccelOptions]; }
    _testGetHwAccelIdx() { return this.hwAccelIdx; }
    _testTryNextHwAccel(reason = "test") { return this.tryNextHwAccel(reason); }
    _testSetHwAccelOptions(options, idx = 0) {
        this.hwAccelOptions = [...options];
        this.hwAccelIdx = idx;
    }
    _testSetVideoTrackForProbe(t) {
        this.videoTrack = {
            id: 1,
            codec: t.codec,
            codecKind: "h264",
            description: t.description,
            timescale: 90000,
            width: t.width,
            height: t.height,
            handler: "vide",
            handlerName: "test",
        };
    }
    async _testRunProbe() {
        return this.probeSupportedConfig();
    }
    _testReorderByCachedGood(supported) {
        return this.reorderByCachedGood(supported);
    }
    static _testRecordWorkingHwAccel(codec, width, height, hint) {
        Playback.recordWorkingHwAccel(codec, width, height, hint);
    }
    static _testRecordRequiresRebuiltAvcC(codec, width, height) {
        Playback.recordRequiresRebuiltAvcC(codec, width, height);
    }
    _testRebuildAvcCFromBytes(bytes) {
        return this.buildAvcCFromNalBytes(bytes);
    }
    _testRebuiltAvcCAttempted() {
        return this.rebuiltAvcCAttempted;
    }
    _testIsFragmented() { return this.isFragmented; }
    // Phase B introspection (state machine + streaming cursor).
    _testGetLoadState() { return this.loadState; }
    _testGetBufferedSampleIdx() { return this.bufferedSampleIdx; }
    _testGetBufferedMaxSec() { return this.bufferedMaxSec; }
    _testGetBodyLen() { return this.bodyLen; }
    _testGetBodyOffset() { return this.bodyOffset; }
    _testGetTotalSize() { return this.totalSize; }
    _testIsPendingPlay() { return this.pendingPlay; }
    _testIsPlaying() { return this.playing; }
    // Force state for unit-style tests that don't drive the full load() flow.
    // Production code must not call these.
    _testForceState(state) { this.loadState = state; }
    _testForceBufferedMaxSec(sec) { this.bufferedMaxSec = sec; }
    _testForceVideoTrack(track) { this.videoTrack = track; }
    _testCallMaybeStartPendingPlay() { this.maybeStartPendingPlay(); }
    static _testGetBodyBytesCeiling() { return Playback.BODY_BYTES_CEILING; }
    static _testGetMinPlayableSeconds() { return Playback.MIN_PLAYABLE_SECONDS; }
}
